import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, startOfWeek, addDays, endOfWeek, parseISO, startOfMonth, endOfMonth, addMonths, isSameMonth } from 'date-fns';
import { shiftApi, employeeSortOrderApi, schedulePublicationStatusApi, schedulingApi, shiftGroupApi, shiftMarketplaceApi, schedulePlaceholderApi } from '../services/api';
import { Shift, ShiftTemplate, Schedule, Employee, Position, PayRecord, BreakTimeSettings, BreakTimeRule, SchedulingApprovedHoliday, SchedulingUnavailability, ScheduleExpectedCountWeek, SchedulePlaceholder, CreateShiftRequest, ShiftGroup, ShiftMarketplaceRequest } from '../types';
import { useSchedulingData, breakTimeRulesFromSettings } from '../hooks/useSchedulingData';
import { ChevronLeft, ChevronRight, Plus, Edit, Trash2, GripVertical, MapPin, X, Copy, Clipboard, MoreVertical, Lock, Unlock, CheckCircle, AlertCircle, Download, Calendar, ArrowDownToLine, Users, Clock, ChevronDown, Loader2, Calculator, RefreshCw, FileUp, History, Trash, UserPlus, Eye, EyeOff } from 'lucide-react';
import { useLocationContext } from './Layout';
import InactiveLocationBanner from './InactiveLocationBanner';
import { WeekNavigator } from './WeekNavigator';
import { DayNavigator } from './DayNavigator';
import { MonthNavigator } from '../pages/OverviewDashboard/MonthNavigator';
import { SchedulingMonthView } from './scheduling/SchedulingMonthView';
import { MarketplaceCoverBadge } from './scheduling/MarketplaceCoverDetailsPopup';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { parseLocalDate, toLocalDateString } from '../utils/payrollPeriodLocal';
import { formatCompanyDate, formatCompanyDateRange } from '../utils/companyDateFormat';
import { WeekPickerCalendar } from './payroll/WeekPickerCalendar';
import { calculateHourlyRateFromPayRecord, getShiftPaidHoursForCost, getShiftLaborCost } from '../utils/laborCost';
import { getShiftTotalHours } from '../utils/shiftHours';
import { extractRestRuleWarnings } from '../utils/restRuleWarnings';
import { RestRuleWarningsModal } from './scheduling/RestRuleWarningsModal';
import {
  buildShiftTemplateColorMap,
  resolveShiftTemplateColor,
  shiftTemplateLightBackground,
  sortShiftTemplatesByStartTime,
} from '../utils/shiftTemplateDisplay';
import ImportScheduleFromFileModal from './ImportScheduleFromFileModal';
import BulkDeleteScheduleShiftsModal from './BulkDeleteScheduleShiftsModal';
import { appConfirm } from '../context/SystemDialogContext';
import {
  indexMarketplaceByShiftId,
  marketplaceEditLockTitle,
} from '../utils/shiftMarketplacePending';
import {
  approvedUnavailabilityBlocksShift,
  formatUnavailabilityBanner,
  shiftConflictsWithUnavailability,
  unavailabilityTooltip,
} from '../utils/unavailability';
import {
  evaluateCurrentWeekExpectedCounts,
  evaluateShiftCreateExpectedCounts,
  normalizeDayCounts,
  summarizeCurrentWeekExpectedCounts,
} from '../utils/scheduleExpectedCounts';
import ScheduleExpectedCountsWeekModal from './ScheduleExpectedCountsWeekModal';
import {
  assigneeFromGridRow,
  assigneeRowId,
  employeesWithVisiblePlaceholders,
  gridEmployeeIdFromShift,
  isPlaceholderRowId,
  loadVisiblePlaceholderIds,
  placeholderIdFromRowId,
  saveVisiblePlaceholderIds,
  toPlaceholderEmployee,
} from '../utils/schedulePlaceholders';

export type SchedulingViewMode = 'week' | 'day' | 'month';

function normalizeWeekExpectedCounts(
  raw: ScheduleExpectedCountWeek | null | undefined,
  scheduleId: number,
  locationId: number,
  weekStart: string
): ScheduleExpectedCountWeek {
  const settings = normalizeDayCounts(raw?.settings);
  const override = raw?.override ? normalizeDayCounts(raw.override) : null;
  return {
    scheduleId: raw?.scheduleId ?? scheduleId,
    locationId: raw?.locationId ?? locationId,
    weekStart: raw?.weekStart ?? weekStart,
    mode: raw?.mode === 'Override' ? 'Override' : 'Settings',
    settings,
    override,
    effective: normalizeDayCounts(raw?.effective || override || settings),
    publicHolidayDates: raw?.publicHolidayDates ?? [],
  };
}

/** API may return string ids/decimals; align shape so totals filters match after POST. */
function normalizeShift(raw: unknown): Shift {
  const s = raw as Record<string, unknown>;
  return {
    ...(s as unknown as Shift),
    id: Number(s.id),
    employeeId: gridEmployeeIdFromShift({
      employeeId: s.employeeId as number | null | undefined,
      placeholderId: s.placeholderId as number | null | undefined,
    }),
    placeholderId: s.placeholderId != null && s.placeholderId !== '' ? Number(s.placeholderId) : undefined,
    locationId: Number(s.locationId),
    scheduleId: Number(s.scheduleId),
    positionId: s.positionId != null && s.positionId !== '' ? Number(s.positionId) : undefined,
    shiftGroupId: s.shiftGroupId != null && s.shiftGroupId !== '' ? Number(s.shiftGroupId) : null,
    paidHours: s.paidHours != null && s.paidHours !== '' ? Number(s.paidHours) : 0,
    unpaidHours: s.unpaidHours != null && s.unpaidHours !== '' ? Number(s.unpaidHours) : 0,
    isPayExtra: Boolean(s.isPayExtra),
    restRuleWarnings: Array.isArray(s.restRuleWarnings)
      ? (s.restRuleWarnings as unknown[]).filter((x): x is string => typeof x === 'string')
      : undefined,
    startTime: String(s.startTime ?? ''),
    endTime: String(s.endTime ?? ''),
  };
}

function isScheduleActive(schedule: Schedule | undefined): boolean {
  return schedule?.isActive === true || schedule?.isActive === null || schedule?.isActive === undefined;
}

function parseLocalDateTime(dateTimeStr: string): Date {
  if (!dateTimeStr.includes('Z') && !dateTimeStr.includes('+') && !dateTimeStr.includes('-', 10)) {
    const [datePart, timePart] = dateTimeStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute, second || 0);
  }
  return parseISO(dateTimeStr);
}

let nextTempShiftId = -1;
function allocateTempShiftId(): number {
  return nextTempShiftId--;
}

function buildShiftTimesForDate(
  date: Date,
  startTimeStr: string,
  endTimeStr: string
): { startTime: string; endTime: string } {
  const dateStr = format(date, 'yyyy-MM-dd');
  const startTime = `${dateStr}T${startTimeStr}:00`;
  let endTime = `${dateStr}T${endTimeStr}:00`;
  const startHour = parseInt(startTimeStr.split(':')[0], 10);
  const endHour = parseInt(endTimeStr.split(':')[0], 10);
  if (endHour < startHour) {
    endTime = `${format(addDays(date, 1), 'yyyy-MM-dd')}T${endTimeStr}:00`;
  }
  return { startTime, endTime };
}

function buildOptimisticShift(tempId: number, payload: CreateShiftRequest): Shift {
  return normalizeShift({
    id: tempId,
    ...payload,
    employeeId: assigneeRowId(payload),
    placeholderId: payload.placeholderId,
    paidHours: 0,
  });
}

function isShiftInWeek(shift: Shift, weekStart: Date, weekEnd: Date): boolean {
  const shiftDate = parseLocalDateTime(shift.startTime);
  return shiftDate >= weekStart && shiftDate <= weekEnd;
}

function countEmployeeScheduleWeekShifts(
  shifts: Shift[],
  scheduleId: number,
  locationId: number,
  employeeId: number | undefined,
  weekStart: Date,
  weekEnd: Date
): number {
  if (employeeId == null || Number.isNaN(Number(employeeId))) return 0;
  return shifts.filter(
    (s) =>
      Number(s.scheduleId) === scheduleId &&
      Number(s.locationId) === locationId &&
      Number(s.employeeId) === Number(employeeId) &&
      isShiftInWeek(s, weekStart, weekEnd)
  ).length;
}

function buildShiftsByEmployeeDate(shifts: Shift[]): Map<number, Map<string, Shift[]>> {
  const index = new Map<number, Map<string, Shift[]>>();
  for (const shift of shifts) {
    const employeeId = Number(shift.employeeId);
    const dateStr = format(parseLocalDateTime(shift.startTime), 'yyyy-MM-dd');
    let dateMap = index.get(employeeId);
    if (!dateMap) {
      dateMap = new Map();
      index.set(employeeId, dateMap);
    }
    let list = dateMap.get(dateStr);
    if (!list) {
      list = [];
      dateMap.set(dateStr, list);
    }
    list.push(shift);
  }
  index.forEach((dateMap) => {
    dateMap.forEach((list) => {
      list.sort(
        (a: Shift, b: Shift) =>
          parseLocalDateTime(a.startTime).getTime() - parseLocalDateTime(b.startTime).getTime()
      );
    });
  });
  return index;
}

function getShiftScheduleName(shift: Shift, schedules: Schedule[]): string {
  return (
    shift.schedule?.name ??
    schedules.find((s) => s.id === shift.scheduleId)?.name ??
    `Schedule ${shift.scheduleId}`
  );
}

function buildSchedulingShiftUrl(shift: Shift, weekAnchor: Date): string {
  const params = new URLSearchParams({
    scheduleId: String(shift.scheduleId),
    locationId: String(shift.locationId),
    week: weekAnchor.toISOString(),
    shiftId: String(shift.id),
  });
  return `/scheduling?${params.toString()}`;
}

function readSchedulingDeepLink(
  search: string
): {
  scheduleId: number | null;
  locationId: number | null;
  week: Date | null;
  shiftId: number | null;
} {
  const params = new URLSearchParams(search);
  const parseId = (key: string): number | null => {
    const raw = params.get(key);
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  let week: Date | null = null;
  const weekParam = params.get('week');
  if (weekParam) {
    const parsedWeek = new Date(weekParam);
    if (!Number.isNaN(parsedWeek.getTime())) {
      week = parsedWeek;
    }
  }

  return {
    scheduleId: parseId('scheduleId'),
    locationId: parseId('locationId'),
    week,
    shiftId: parseId('shiftId'),
  };
}

const ScheduleCalendar: React.FC = () => {
  const weekStartsOn = useWeekStartDay(); // Universal week start day setting
  const [searchParams] = useSearchParams();
  const { debouncedSelectedLocationId, selectedLocationId, setSelectedLocationId, locations, isSelectedLocationInactive, selectedLocation } = useLocationContext();
  const initialDeepLink = useMemo(
    () => readSchedulingDeepLink(window.location.search),
    []
  );
  const targetShiftIdFromUrl = useRef<number | null>(initialDeepLink.shiftId);
  const pendingDeepLinkScheduleId = useRef<number | null>(initialDeepLink.scheduleId);
  const hasScrolledToTargetShift = useRef(false);
  
  // Load persisted week from localStorage
  const getInitialWeek = (): Date => {
    const savedWeek = localStorage.getItem('scheduling_currentWeek');
    if (savedWeek) {
      try {
        const parsedDate = new Date(savedWeek);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      } catch (e) {
        console.error('Error parsing saved week:', e);
      }
    }
    return new Date();
  };
  
  // Load persisted schedule ID from localStorage
  const getInitialScheduleId = (): number | null => {
    const savedScheduleId = localStorage.getItem('scheduling_selectedScheduleId');
    if (savedScheduleId) {
      try {
        const parsedId = Number(savedScheduleId);
        if (!isNaN(parsedId)) {
          return parsedId;
        }
      } catch (e) {
        console.error('Error parsing saved schedule ID:', e);
      }
    }
    return null;
  };
  
  const [currentWeek, setCurrentWeek] = useState<Date>(initialDeepLink.week ?? getInitialWeek());
  const getInitialViewMode = (): SchedulingViewMode => {
    const saved = localStorage.getItem('scheduling_viewMode');
    if (saved === 'week' || saved === 'day' || saved === 'month') return saved;
    return 'week';
  };
  const [viewMode, setViewMode] = useState<SchedulingViewMode>(getInitialViewMode);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(
    initialDeepLink.scheduleId ?? getInitialScheduleId()
  );
  const [draggedTemplate, setDraggedTemplate] = useState<ShiftTemplate | null>(null);
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    startTime: '',
    endTime: '',
    hours: 0,
    unpaidBreakMins: 0,
    breakTimePaid: false,
    breakUseGlobalSettings: true,
    totalHours: 0,
    color: '#3b82f6',
    displayOption: 'time' as 'time' | 'f' | 'close',
    positionId: undefined as number | undefined,
    shiftGroupId: undefined as number | undefined,
    notes: '',
    isManualColorOverride: false,
    originalColor: '#3b82f6', // Store original color for reset
    isPayExtra: false,
  });
  const [shiftGroupsForLocation, setShiftGroupsForLocation] = useState<ShiftGroup[]>([]);
  const [showCreateShiftGroupModal, setShowCreateShiftGroupModal] = useState(false);
  const [newShiftGroupName, setNewShiftGroupName] = useState('');
  const [creatingShiftGroup, setCreatingShiftGroup] = useState(false);

  useEffect(() => {
    if (!showShiftForm || !editingShift?.locationId) {
      setShiftGroupsForLocation([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const groups = await shiftGroupApi.getAll({ locationId: editingShift.locationId });
        if (!cancelled) setShiftGroupsForLocation(groups || []);
      } catch (error) {
        console.error('Error loading shift groups:', error);
        if (!cancelled) setShiftGroupsForLocation([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showShiftForm, editingShift?.locationId]);

  // Break time settings state
  const [breakTimeSettings, setBreakTimeSettings] = useState<BreakTimeSettings | null>(null);
  const [breakTimeRules, setBreakTimeRules] = useState<BreakTimeRule[]>([]);
  const [breakMinsManuallySet, setBreakMinsManuallySet] = useState(false);

  // Calendar week (publish / sort / import) always follows the week containing the anchor date
  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekStartStr = useMemo(() => format(weekStart, 'yyyy-MM-dd'), [weekStart]);
  const weekEndStr = useMemo(() => format(weekEnd, 'yyyy-MM-dd'), [weekEnd]);

  // Data range: full week for week/day (day shows one column); month for month view
  const viewRangeStart = useMemo(
    () => (viewMode === 'month' ? startOfMonth(currentWeek) : weekStart),
    [viewMode, currentWeek, weekStart]
  );
  const viewRangeEnd = useMemo(
    () => (viewMode === 'month' ? endOfMonth(currentWeek) : weekEnd),
    [viewMode, currentWeek, weekEnd]
  );
  const viewRangeStartStr = useMemo(() => format(viewRangeStart, 'yyyy-MM-dd'), [viewRangeStart]);
  const viewRangeEndStr = useMemo(() => format(viewRangeEnd, 'yyyy-MM-dd'), [viewRangeEnd]);

  const calendarWeekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  // Columns shown in the employee grid (one day in Daily, full week in Weekly)
  const weekDays = useMemo(() => {
    if (viewMode === 'day') {
      return [currentWeek];
    }
    return calendarWeekDays;
  }, [viewMode, currentWeek, calendarWeekDays]);

  const gridTemplateColumns = useMemo(() => {
    // Employee | day columns | Total
    const dayCount = weekDays.length;
    return `minmax(140px, 1.2fr) repeat(${dayCount}, minmax(100px, 1fr)) minmax(90px, 0.8fr)`;
  }, [weekDays.length]);

  const {
    bootstrap,
    weekView,
    loadingBootstrap,
    loadingWeekView,
    reloadWeekView,
  } = useSchedulingData(
    debouncedSelectedLocationId,
    selectedScheduleId,
    viewRangeStartStr,
    viewRangeEndStr
  );
  
  // Use ref to prevent duplicate location setting
  const hasSetLocationRef = useRef(false);

  // State for data
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const shiftsRef = useRef<Shift[]>([]);
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplate[]>([]);
  const sortedShiftTemplates = useMemo(() => {
    const colorMap = buildShiftTemplateColorMap(shiftTemplates);
    return sortShiftTemplatesByStartTime(
      shiftTemplates.map((template) => ({
        ...template,
        color: resolveShiftTemplateColor(template, colorMap),
      }))
    );
  }, [shiftTemplates]);
  const [positions, setPositions] = useState<any[]>([]);
  const [scheduleCounts, setScheduleCounts] = useState<Record<number, { totalShifts: number; uniqueEmployees: number; totalEmployeesAssigned: number }>>({});
  const [weekExpectedCounts, setWeekExpectedCounts] = useState<ScheduleExpectedCountWeek | null>(null);
  const [showExpectedCountsModal, setShowExpectedCountsModal] = useState(false);
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [placeholderList, setPlaceholderList] = useState<SchedulePlaceholder[]>([]);
  const [placeholderDraft, setPlaceholderDraft] = useState('');
  const [editingPlaceholder, setEditingPlaceholder] = useState<SchedulePlaceholder | null>(null);
  const [savingPlaceholder, setSavingPlaceholder] = useState(false);
  const [visiblePlaceholderIds, setVisiblePlaceholderIds] = useState<number[]>([]);
  const visiblePlaceholderIdsRef = useRef<number[]>([]);
  const [assignSeat, setAssignSeat] = useState<{ id: number; name: string } | null>(null);
  const [assignEmployeeId, setAssignEmployeeId] = useState(0);
  const [assignQuery, setAssignQuery] = useState('');
  const [assigningSeat, setAssigningSeat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSortModal, setShowSortModal] = useState(false);
  const [employeeSortOrder, setEmployeeSortOrder] = useState<{ employeeId: number; sortOrder: number }[]>([]);
  const [draggedEmployeeId, setDraggedEmployeeId] = useState<number | null>(null);
  const [dragOverEmployeeId, setDragOverEmployeeId] = useState<number | null>(null);
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  const [hoveredShiftId, setHoveredShiftId] = useState<number | null>(null);
  const [copiedShift, setCopiedShift] = useState<Shift | null>(null);
  const [publicationStatus, setPublicationStatus] = useState<{ status: string; id?: number; publishedAt?: string; changedAt?: string } | null>(null);
  const [allSchedulesPublicationStatus, setAllSchedulesPublicationStatus] = useState<Record<number, { status: string; id?: number; publishedAt?: string; changedAt?: string }>>({});
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showUnlockWarning, setShowUnlockWarning] = useState(false);
  const [restRuleWarningModal, setRestRuleWarningModal] = useState<{
    warnings: string[];
    shiftId?: number;
  } | null>(null);
  const [pendingEditAction, setPendingEditAction] = useState<(() => void) | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [totalDisplayMode, setTotalDisplayMode] = useState<'hours' | 'cost'>('hours');
  const [showTotalDropdown, setShowTotalDropdown] = useState(false);
  const [employeePayRecords, setEmployeePayRecords] = useState<Record<number, PayRecord>>({});
  const totalHeaderDropdownRef = useRef<HTMLDivElement>(null);
  const [showCostBreakdownModal, setShowCostBreakdownModal] = useState(false);
  const [selectedEmployeeForBreakdown, setSelectedEmployeeForBreakdown] = useState<Employee | null>(null);
  const [savingShift, setSavingShift] = useState(false);
  const [showScheduleBreakdownModal, setShowScheduleBreakdownModal] = useState(false);
  const [locationEmployees, setLocationEmployees] = useState<Employee[]>([]);
  const [locationShifts, setLocationShifts] = useState<Shift[]>([]);
  const [locationPayRecords, setLocationPayRecords] = useState<Record<number, PayRecord>>({});
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (totalHeaderDropdownRef.current && !totalHeaderDropdownRef.current.contains(event.target as Node)) {
        setShowTotalDropdown(false);
      }
    };

    if (showTotalDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTotalDropdown]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importWeekPickerOpen, setImportWeekPickerOpen] = useState(false);
  const [importWeekStart, setImportWeekStart] = useState<Date | null>(null);
  const [importShifts, setImportShifts] = useState<Shift[]>([]);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importWeekInProgress, setImportWeekInProgress] = useState(false);
  const importWeekInProgressRef = useRef(false);
  const [showScheduleFileImportModal, setShowScheduleFileImportModal] = useState(false);
  const [showBulkDeleteScheduleShiftsModal, setShowBulkDeleteScheduleShiftsModal] = useState(false);
  const [colorMode, setColorMode] = useState<'shift' | 'position'>('shift');
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [shiftIdToDelete, setShiftIdToDelete] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [marketplaceRequests, setMarketplaceRequests] = useState<ShiftMarketplaceRequest[]>([]);

  // Set first location as default if none selected (only once)
  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId && !hasSetLocationRef.current) {
      setSelectedLocationId(locations[0].id);
      hasSetLocationRef.current = true;
    }
  }, [locations, selectedLocationId, setSelectedLocationId]);

  // Persist current week to localStorage
  useEffect(() => {
    localStorage.setItem('scheduling_currentWeek', currentWeek.toISOString());
  }, [currentWeek]);

  useEffect(() => {
    localStorage.setItem('scheduling_viewMode', viewMode);
  }, [viewMode]);

  // Persist selected schedule ID to localStorage
  useEffect(() => {
    if (selectedScheduleId !== null) {
      localStorage.setItem('scheduling_selectedScheduleId', selectedScheduleId.toString());
    }
  }, [selectedScheduleId]);

  // Month view returns publication keyed by month start — refresh using calendar week start
  useEffect(() => {
    if (viewMode !== 'month' || !debouncedSelectedLocationId || !selectedScheduleId) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await schedulePublicationStatusApi.get(
          debouncedSelectedLocationId,
          selectedScheduleId,
          weekStartStr
        );
        if (!cancelled) setPublicationStatus(status);
        const statuses = await schedulingApi.getPublicationStatuses(debouncedSelectedLocationId, weekStartStr);
        if (!cancelled) setAllSchedulesPublicationStatus(statuses);
      } catch (error) {
        console.error('Error refreshing publication status for month view:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewMode, debouncedSelectedLocationId, selectedScheduleId, weekStartStr, weekView]);

  // Deep-link: apply location before first data fetch (full page load + in-app navigation)
  useLayoutEffect(() => {
    const deepLink = readSchedulingDeepLink(searchParams.toString());
    if (deepLink.locationId != null) {
      setSelectedLocationId(deepLink.locationId);
    }
  }, [searchParams, setSelectedLocationId]);

  useEffect(() => {
    const deepLink = readSchedulingDeepLink(searchParams.toString());

    if (deepLink.scheduleId != null) {
      pendingDeepLinkScheduleId.current = deepLink.scheduleId;
      setSelectedScheduleId(deepLink.scheduleId);
    }

    if (deepLink.week) {
      setCurrentWeek(deepLink.week);
    }

    if (deepLink.shiftId != null) {
      targetShiftIdFromUrl.current = deepLink.shiftId;
      hasScrolledToTargetShift.current = false;
    } else {
      targetShiftIdFromUrl.current = null;
    }
  }, [searchParams]);

  // Close position dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showPositionDropdown && !target.closest('.position-dropdown-container')) {
        setShowPositionDropdown(false);
      }
    };

    if (showPositionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPositionDropdown]);

  useEffect(() => {
    if (!bootstrap) return;
    setSchedules(bootstrap.schedules);
    setPositions(bootstrap.positions);
    setBreakTimeSettings(bootstrap.breakTimeSettings);
    setBreakTimeRules(breakTimeRulesFromSettings(bootstrap.breakTimeSettings));
  }, [bootstrap]);

  useEffect(() => {
    if (!weekView) {
      if (!loadingWeekView && !debouncedSelectedLocationId) {
        setEmployees([]);
        setShifts([]);
        setShiftTemplates([]);
        setScheduleCounts({});
        setEmployeeSortOrder([]);
        setPublicationStatus(null);
        setAllSchedulesPublicationStatus({});
        setEmployeePayRecords({});
        setLocationEmployees([]);
        setLocationShifts([]);
        setLocationPayRecords({});
      }
      return;
    }

    setEmployees(
      employeesWithVisiblePlaceholders(
        weekView.placeholders ?? [],
        weekView.employees,
        visiblePlaceholderIdsRef.current
      )
    );
    if ((weekView.placeholders ?? []).length > 0) {
      setPlaceholderList((prev) => {
        const usedById = new Map(prev.map((p) => [p.id, p.isUsed]));
        return (weekView.placeholders ?? []).map((p) => ({
          ...p,
          isUsed: usedById.get(p.id) ?? p.isUsed,
        }));
      });
    }
    setShifts((prev) => {
      const warnById = new Map(
        prev
          .filter((s) => s.id > 0 && s.restRuleWarnings?.length)
          .map((s) => [s.id, s.restRuleWarnings!] as const)
      );
      const serverShifts = weekView.shifts.map((raw) => {
        const s = normalizeShift(raw);
        const warnings = warnById.get(s.id);
        return warnings ? { ...s, restRuleWarnings: warnings } : s;
      });
      const pendingOptimistic = prev.filter((s) => s.id < 0);
      return pendingOptimistic.length > 0 ? [...serverShifts, ...pendingOptimistic] : serverShifts;
    });
    setShiftTemplates(weekView.shiftTemplates);
    setScheduleCounts(weekView.scheduleCounts);
    setEmployeeSortOrder(weekView.sortOrder);
    setPublicationStatus(weekView.selectedPublicationStatus);
    setAllSchedulesPublicationStatus(weekView.publicationStatuses);
    setEmployeePayRecords(weekView.payRecords);
    setLocationEmployees(weekView.locationEmployees);
    setLocationShifts(weekView.locationShifts.map(normalizeShift));
    setLocationPayRecords(weekView.locationPayRecords);
    setWeekExpectedCounts(
      normalizeWeekExpectedCounts(
        weekView.expectedCounts,
        Number(weekView.expectedCounts?.scheduleId) || 0,
        Number(weekView.expectedCounts?.locationId) || debouncedSelectedLocationId || 0,
        weekView.expectedCounts?.weekStart || ''
      )
    );
  }, [weekView, loadingWeekView, debouncedSelectedLocationId]);

  const marketplaceByShiftId = useMemo(
    () => indexMarketplaceByShiftId(marketplaceRequests),
    [marketplaceRequests]
  );

  const loadMarketplaceRequests = useCallback(async () => {
    if (!debouncedSelectedLocationId) {
      setMarketplaceRequests([]);
      return;
    }
    try {
      const results = await Promise.allSettled([
        shiftMarketplaceApi.getRequests({ locationId: debouncedSelectedLocationId, status: 'Open' }),
        shiftMarketplaceApi.getRequests({ locationId: debouncedSelectedLocationId, status: 'PendingApproval' }),
      ]);
      const rows = results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
      setMarketplaceRequests(rows);
    } catch {
      setMarketplaceRequests([]);
    }
  }, [debouncedSelectedLocationId]);

  useEffect(() => {
    void loadMarketplaceRequests();
  }, [loadMarketplaceRequests, weekStartStr]);

  useEffect(() => {
    shiftsRef.current = shifts;
  }, [shifts]);

  const refreshScheduleCountsInBackground = useCallback(() => {
    if (!debouncedSelectedLocationId) return;
    void (async () => {
      try {
        const countsData = await shiftApi.getCounts({
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: debouncedSelectedLocationId,
        });
        const countsMap: Record<number, { totalShifts: number; uniqueEmployees: number; totalEmployeesAssigned: number }> = {};
        countsData.forEach((item) => {
          countsMap[item.scheduleId] = {
            totalShifts: item.totalShifts,
            uniqueEmployees: item.uniqueEmployees,
            totalEmployeesAssigned: item.totalEmployeesAssigned || 0,
          };
        });
        setScheduleCounts(countsMap);
      } catch (error) {
        console.error('Error fetching schedule counts:', error);
      }
    })();
  }, [debouncedSelectedLocationId, weekStartStr, weekEndStr]);

  const bumpScheduleCountsForAdd = useCallback((payload: CreateShiftRequest) => {
    const employeeShiftCount = countEmployeeScheduleWeekShifts(
      shiftsRef.current,
      payload.scheduleId,
      payload.locationId,
      assigneeRowId(payload),
      weekStart,
      weekEnd
    );
    setScheduleCounts((prev) => {
      const current = prev[payload.scheduleId] ?? { totalShifts: 0, uniqueEmployees: 0, totalEmployeesAssigned: 0 };
      return {
        ...prev,
        [payload.scheduleId]: {
          ...current,
          totalShifts: current.totalShifts + 1,
          uniqueEmployees: current.uniqueEmployees + (employeeShiftCount === 0 ? 1 : 0),
        },
      };
    });
  }, [weekStart, weekEnd]);

  const bumpScheduleCountsForRemove = useCallback((shift: Shift) => {
    const employeeShiftCount = countEmployeeScheduleWeekShifts(
      shiftsRef.current,
      shift.scheduleId,
      shift.locationId,
      shift.employeeId,
      weekStart,
      weekEnd
    );
    setScheduleCounts((prev) => {
      const current = prev[shift.scheduleId] ?? { totalShifts: 0, uniqueEmployees: 0, totalEmployeesAssigned: 0 };
      return {
        ...prev,
        [shift.scheduleId]: {
          ...current,
          totalShifts: Math.max(0, current.totalShifts - 1),
          uniqueEmployees: Math.max(0, current.uniqueEmployees - (employeeShiftCount === 1 ? 1 : 0)),
        },
      };
    });
  }, [weekStart, weekEnd]);

  // Schedule counts for dropdown (active + inactive-with-shifts) — load as soon as location/week are known
  useEffect(() => {
    if (!debouncedSelectedLocationId) {
      setScheduleCounts({});
      return;
    }
    if (weekView) return;

    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const countsData = await shiftApi.getCounts({
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: debouncedSelectedLocationId,
        });
        if (cancelled) return;

        const countsMap: Record<number, { totalShifts: number; uniqueEmployees: number; totalEmployeesAssigned: number }> = {};
        countsData.forEach((item) => {
          countsMap[item.scheduleId] = {
            totalShifts: item.totalShifts,
            uniqueEmployees: item.uniqueEmployees,
            totalEmployeesAssigned: item.totalEmployeesAssigned || 0,
          };
        });
        if (!weekView) {
          setScheduleCounts(countsMap);
        }
      } catch (error) {
        console.error('Error fetching schedule counts:', error);
      }
    };

    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [debouncedSelectedLocationId, weekStartStr, weekEndStr, weekView]);

  // Publication status icons in schedule dropdown (before a schedule is selected / week-view loads)
  useEffect(() => {
    if (!debouncedSelectedLocationId) {
      return;
    }
    if (weekView) return;

    let cancelled = false;
    const loadPublicationStatuses = async () => {
      try {
        const statuses = await schedulingApi.getPublicationStatuses(debouncedSelectedLocationId, weekStartStr);
        if (!cancelled && !weekView) {
          setAllSchedulesPublicationStatus(statuses);
        }
      } catch (error) {
        console.error('Error fetching publication statuses:', error);
      }
    };

    loadPublicationStatuses();
    return () => {
      cancelled = true;
    };
  }, [debouncedSelectedLocationId, weekStartStr, weekView]);

  const shiftsByEmployeeDate = useMemo(() => buildShiftsByEmployeeDate(shifts), [shifts]);

  const approvedHolidayIndex = useMemo(() => {
    const index = new Map<number, Map<string, SchedulingApprovedHoliday>>();
    for (const holiday of weekView?.approvedHolidays ?? []) {
      const employeeId = Number(holiday.employeeId);
      const dateStr = holiday.date.split('T')[0];
      let dateMap = index.get(employeeId);
      if (!dateMap) {
        dateMap = new Map();
        index.set(employeeId, dateMap);
      }
      dateMap.set(dateStr, holiday);
    }
    return index;
  }, [weekView?.approvedHolidays]);

  const getApprovedHoliday = useCallback(
    (employeeId: number, date: Date): SchedulingApprovedHoliday | undefined => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return approvedHolidayIndex.get(Number(employeeId))?.get(dateStr);
    },
    [approvedHolidayIndex]
  );

  const hasApprovedHolidayOnDate = useCallback(
    (employeeId: number, date: Date): boolean => !!getApprovedHoliday(employeeId, date),
    [getApprovedHoliday]
  );

  const unavailabilityIndex = useMemo(() => {
    const index = new Map<number, Map<string, SchedulingUnavailability[]>>();
    for (const item of weekView?.unavailability ?? []) {
      const employeeId = Number(item.employeeId);
      const dateStr = item.date.split('T')[0];
      let dateMap = index.get(employeeId);
      if (!dateMap) {
        dateMap = new Map();
        index.set(employeeId, dateMap);
      }
      const list = dateMap.get(dateStr) ?? [];
      list.push(item);
      dateMap.set(dateStr, list);
    }
    return index;
  }, [weekView?.unavailability]);

  const getUnavailabilityFor = useCallback(
    (employeeId: number, date: Date): SchedulingUnavailability[] => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return unavailabilityIndex.get(Number(employeeId))?.get(dateStr) ?? [];
    },
    [unavailabilityIndex]
  );

  const hasApprovedAllDayUnavailability = useCallback(
    (employeeId: number, date: Date): boolean =>
      getUnavailabilityFor(employeeId, date).some((u) => u.status === 'Approved' && u.isAllDay),
    [getUnavailabilityFor]
  );

  const employeeWeekUnavailability = useCallback(
    (employeeId: number): SchedulingUnavailability[] => {
      const dateMap = unavailabilityIndex.get(Number(employeeId));
      if (!dateMap) return [];
      return weekDays.flatMap((day) => dateMap.get(format(day, 'yyyy-MM-dd')) ?? []);
    },
    [unavailabilityIndex, weekDays]
  );
  const totalsCacheRef = useRef<{
    employeeHoursCurrent: Record<number, number>;
    employeeCostCurrent: Record<number, number>;
    employeeHoursAll: Record<number, number>;
    employeeCostAll: Record<number, number>;
    dayHoursCurrent: Record<string, number>;
    dayCostCurrent: Record<string, number>;
    dayHoursAll: Record<string, number>;
    dayCostAll: Record<string, number>;
    grandHoursCurrent: number;
    grandCostCurrent: number;
    grandHoursAll: number;
    grandCostAll: number;
  } | null>(null);

  // Sync the selected schedule's publication status
  useEffect(() => {
    if (selectedScheduleId !== null && publicationStatus) {
      setAllSchedulesPublicationStatus(prev => ({
        ...prev,
        [selectedScheduleId]: publicationStatus,
      }));
    }
  }, [selectedScheduleId, publicationStatus]);

  const dropdownSchedules = useMemo(() => {
    const allSchedules = bootstrap?.schedules?.length ? bootstrap.schedules : schedules;
    return allSchedules
      .filter((sched) => {
        if (isScheduleActive(sched)) return true;
        return (scheduleCounts[sched.id]?.totalShifts ?? 0) > 0;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [bootstrap, schedules, scheduleCounts]);

  const selectedSchedule = useMemo(
    () => schedules.find((s) => s.id === selectedScheduleId),
    [schedules, selectedScheduleId]
  );

  const isScheduleReadOnly = useMemo(
    () => selectedSchedule != null && !isScheduleActive(selectedSchedule),
    [selectedSchedule]
  );

  const isReadOnly = isScheduleReadOnly || isSelectedLocationInactive;

  useEffect(() => {
    if (!selectedScheduleId || !debouncedSelectedLocationId) {
      setWeekExpectedCounts(null);
    }
  }, [selectedScheduleId, debouncedSelectedLocationId]);

  const expectedCountWarning = useMemo(() => {
    if (!selectedScheduleId || !debouncedSelectedLocationId) return null;
    return evaluateCurrentWeekExpectedCounts({
      week: weekExpectedCounts,
      shifts,
      scheduleId: selectedScheduleId,
      locationId: debouncedSelectedLocationId,
      weekStart: weekStartStr,
      weekEnd: weekEndStr,
    });
  }, [
    weekExpectedCounts,
    shifts,
    selectedScheduleId,
    debouncedSelectedLocationId,
    weekStartStr,
    weekEndStr,
  ]);

  const expectedCountProgress = useMemo(() => {
    if (!selectedScheduleId || !debouncedSelectedLocationId) return null;
    return summarizeCurrentWeekExpectedCounts({
      week: weekExpectedCounts,
      shifts,
      scheduleId: selectedScheduleId,
      locationId: debouncedSelectedLocationId,
      weekStart: weekStartStr,
      weekEnd: weekEndStr,
    });
  }, [
    weekExpectedCounts,
    shifts,
    selectedScheduleId,
    debouncedSelectedLocationId,
    weekStartStr,
    weekEndStr,
  ]);

  const loadPlaceholderList = useCallback(async () => {
    if (!selectedScheduleId) {
      setPlaceholderList([]);
      return;
    }
    try {
      const rows = await schedulePlaceholderApi.getAll(selectedScheduleId);
      setPlaceholderList(rows || []);
    } catch (error) {
      console.error('Error loading placeholders:', error);
      setPlaceholderList([]);
    }
  }, [selectedScheduleId]);

  useEffect(() => {
    const ids = selectedScheduleId ? loadVisiblePlaceholderIds(selectedScheduleId) : [];
    visiblePlaceholderIdsRef.current = ids;
    setVisiblePlaceholderIds(ids);
  }, [selectedScheduleId]);

  const setPlaceholderVisibility = (placeholderId: number, show: boolean) => {
    const next = show
      ? [...visiblePlaceholderIds.filter((id) => id !== placeholderId), placeholderId]
      : visiblePlaceholderIds.filter((id) => id !== placeholderId);
    visiblePlaceholderIdsRef.current = next;
    setVisiblePlaceholderIds(next);
    if (selectedScheduleId) saveVisiblePlaceholderIds(selectedScheduleId, next);

    const row = placeholderList.find((p) => p.id === placeholderId);
    if (show && row) {
      setEmployees((prev) => {
        if (prev.some((e) => e.placeholderId === placeholderId)) return prev;
        return [toPlaceholderEmployee(row), ...prev];
      });
      return;
    }
    setEmployees((prev) => prev.filter((e) => e.placeholderId !== placeholderId));
  };

  const openPlaceholderModal = async () => {
    setEditingPlaceholder(null);
    setPlaceholderDraft('');
    setShowPlaceholderModal(true);
    await loadPlaceholderList();
  };

  const savePlaceholder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedScheduleId || !placeholderDraft.trim()) {
      alert('Enter a placeholder name.');
      return;
    }
    setSavingPlaceholder(true);
    try {
      const name = placeholderDraft.trim();
      if (editingPlaceholder) {
        await schedulePlaceholderApi.update(editingPlaceholder.id, {
          scheduleId: selectedScheduleId,
          name,
        });
        setPlaceholderList((prev) =>
          prev.map((p) => (p.id === editingPlaceholder.id ? { ...p, name } : p))
        );
        setEmployees((prev) =>
          prev.map((e) => (e.placeholderId === editingPlaceholder.id ? { ...e, firstName: name } : e))
        );
      } else {
        const created = await schedulePlaceholderApi.create({
          scheduleId: selectedScheduleId,
          name,
        });
        setPlaceholderList((prev) =>
          [...prev.filter((p) => p.id !== created.id), created].sort((a, b) => a.name.localeCompare(b.name))
        );
      }
      setEditingPlaceholder(null);
      setPlaceholderDraft('');
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to save placeholder.');
    } finally {
      setSavingPlaceholder(false);
    }
  };

  const deletePlaceholder = async (row: SchedulePlaceholder) => {
    if (row.isUsed) {
      alert('This placeholder is used on one or more shifts and cannot be deleted.');
      return;
    }
    if (!(await appConfirm(`Remove placeholder "${row.name}"?`, { danger: true, confirmLabel: 'Delete' }))) {
      return;
    }
    try {
      await schedulePlaceholderApi.delete(row.id);
      setPlaceholderList((prev) => prev.filter((p) => p.id !== row.id));
      setEmployees((prev) => prev.filter((e) => e.placeholderId !== row.id));
      if (visiblePlaceholderIds.includes(row.id)) {
        setPlaceholderVisibility(row.id, false);
      }
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to delete placeholder.');
    }
  };

  const assignCandidates = useMemo(() => {
    const q = assignQuery.trim().toLowerCase();
    return employees
      .filter((emp) => !emp.isPlaceholder)
      .filter((emp) => {
        if (!q) return true;
        return `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(q) || emp.email.toLowerCase().includes(q);
      })
      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  }, [employees, assignQuery]);

  const assignSeatThisWeekCount = useMemo(() => {
    if (!assignSeat) return 0;
    return shifts.filter(
      (shift) =>
        Number(shift.placeholderId) === assignSeat.id &&
        Number(shift.locationId) === Number(debouncedSelectedLocationId) &&
        isShiftInWeek(shift, weekStart, weekEnd)
    ).length;
  }, [assignSeat, shifts, debouncedSelectedLocationId, weekStart, weekEnd]);

  const confirmAssignSeat = async () => {
    if (!assignSeat || !assignEmployeeId) {
      alert('Select a person to assign.');
      return;
    }
    if (assignSeatThisWeekCount === 0) {
      alert('This seat has no shifts this week to assign.');
      return;
    }
    const person = employees.find((emp) => emp.id === assignEmployeeId);
    const personName = person ? `${person.firstName} ${person.lastName}`.trim() : 'this person';
    if (
      !(await appConfirm(
        `Assign ${assignSeatThisWeekCount} shift${assignSeatThisWeekCount === 1 ? '' : 's'} on “${assignSeat.name}” this week to ${personName}?`,
        { confirmLabel: 'Assign' }
      ))
    ) {
      return;
    }
    const personId = Number(assignEmployeeId);
    if (!Number.isFinite(personId) || personId <= 0 || isPlaceholderRowId(personId)) {
      alert('Select a person to assign.');
      return;
    }
    setAssigningSeat(true);
    try {
      const seatId = assignSeat.id;
      const locId = Number(debouncedSelectedLocationId);
      await schedulePlaceholderApi.assign(seatId, {
        employeeId: personId,
        locationId: debouncedSelectedLocationId || undefined,
        fromDate: weekStartStr,
        toDate: weekEndStr,
      });
      const moveShift = (shift: Shift): Shift => {
        if (
          Number(shift.placeholderId) !== seatId ||
          Number(shift.locationId) !== locId ||
          !isShiftInWeek(shift, weekStart, weekEnd)
        ) {
          return shift;
        }
        return normalizeShift({
          ...shift,
          employeeId: personId,
          placeholderId: null,
        });
      };
      setShifts((prev) => {
        const next = prev.map(moveShift);
        shiftsRef.current = next;
        return next;
      });
      setLocationShifts((prev) => prev.map(moveShift));
      setPlaceholderVisibility(seatId, false);
      setAssignSeat(null);
      setAssignEmployeeId(0);
      setAssignQuery('');
      markScheduleChangedInBackground();
      refreshScheduleCountsInBackground();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to assign person to this seat.');
    } finally {
      setAssigningSeat(false);
    }
  };

  useEffect(() => {
    if (isReadOnly) {
      setShowShiftForm(false);
      setEditingShift(null);
      setCopiedShift(null);
      setShowActionsMenu(false);
      setShowSortModal(false);
      setAssignSeat(null);
    }
  }, [isReadOnly]);

  useEffect(() => {
    const pendingScheduleId = pendingDeepLinkScheduleId.current;

    if (dropdownSchedules.length === 0) {
      if (!pendingScheduleId && selectedScheduleId !== null) {
        setSelectedScheduleId(null);
      }
      return;
    }

    if (pendingScheduleId != null) {
      const deepLinkSchedule = dropdownSchedules.find((s) => s.id === pendingScheduleId);
      if (deepLinkSchedule) {
        if (selectedScheduleId !== pendingScheduleId) {
          setSelectedScheduleId(pendingScheduleId);
        }
        pendingDeepLinkScheduleId.current = null;
        return;
      }
      return;
    }

    const isSelectedInDropdown =
      selectedScheduleId != null && dropdownSchedules.some((s) => s.id === selectedScheduleId);
    if (isSelectedInDropdown) return;

    const savedScheduleId = localStorage.getItem('scheduling_selectedScheduleId');
    if (savedScheduleId) {
      const parsedId = Number(savedScheduleId);
      if (!isNaN(parsedId) && dropdownSchedules.some((s) => s.id === parsedId)) {
        setSelectedScheduleId(parsedId);
        return;
      }
    }

    setSelectedScheduleId(dropdownSchedules[0].id);
  }, [dropdownSchedules, selectedScheduleId]);

  // Employees are pre-filtered by the week-view API (or fallback). Apply read-only shift filter, search, and sort only.
  const filteredEmployees = useMemo(() => {
    if (!debouncedSelectedLocationId || !selectedScheduleId) return [];

    let filtered = employees;

    if (isScheduleReadOnly) {
      const employeeIdsWithCurrentScheduleShifts = new Set(
        shifts
          .filter(
            (shift) =>
              Number(shift.scheduleId) === Number(selectedScheduleId) &&
              Number(shift.locationId) === Number(debouncedSelectedLocationId)
          )
          .map((shift) => shift.employeeId)
      );
      filtered = filtered.filter((emp) => employeeIdsWithCurrentScheduleShifts.has(emp.id));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (emp) =>
          `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(query) ||
          emp.email.toLowerCase().includes(query)
      );
    }

    if (employeeSortOrder.length > 0) {
      filtered = [...filtered].sort((a, b) => {
        const orderA = employeeSortOrder.find((o) => o.employeeId === a.id)?.sortOrder ?? 999;
        const orderB = employeeSortOrder.find((o) => o.employeeId === b.id)?.sortOrder ?? 999;
        return orderA - orderB;
      });
    }

    return filtered;
  }, [employees, debouncedSelectedLocationId, selectedScheduleId, searchQuery, employeeSortOrder, isScheduleReadOnly, shifts]);

  useEffect(() => {
    if (
      hasScrolledToTargetShift.current ||
      targetShiftIdFromUrl.current === null ||
      loadingWeekView ||
      filteredEmployees.length === 0
    ) {
      return;
    }

    const shiftId = targetShiftIdFromUrl.current;

    const scrollToShift = (): boolean => {
      const shiftElement = document.getElementById(`schedule-shift-${shiftId}`);
      if (!shiftElement) {
        return false;
      }

      shiftElement.scrollIntoView({ block: 'center' });
      hasScrolledToTargetShift.current = true;
      targetShiftIdFromUrl.current = null;
      return true;
    };

    const timeoutId = window.setTimeout(() => {
      if (!scrollToShift()) {
        window.setTimeout(() => {
          scrollToShift();
        }, 500);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [loadingWeekView, filteredEmployees, shifts, selectedScheduleId, debouncedSelectedLocationId]);

  const isCurrentScheduleShift = (shift: Shift): boolean =>
    Number(shift.scheduleId) === Number(selectedScheduleId) &&
    Number(shift.locationId) === Number(debouncedSelectedLocationId);

  const currentScheduleWeekShiftCount = useMemo(
    () =>
      shifts.filter(
        (s) =>
          Number(s.scheduleId) === Number(selectedScheduleId) &&
          Number(s.locationId) === Number(debouncedSelectedLocationId)
      ).length,
    [shifts, debouncedSelectedLocationId, selectedScheduleId]
  );

  const isSameLocationShift = (shift: Shift): boolean =>
    Number(shift.locationId) === Number(debouncedSelectedLocationId);

  const getShiftsForEmployeeAndDate = useCallback(
    (employeeId: number, date: Date): Shift[] => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return shiftsByEmployeeDate.get(Number(employeeId))?.get(dateStr) ?? [];
    },
    [shiftsByEmployeeDate]
  );

  // Check if a shift time overlaps with existing shifts (uses loaded week-view shift cache)
  const checkTimeOverlap = (employeeId: number, date: Date, newStartTime: string, newEndTime: string, excludeShiftId?: number): boolean => {
    const allEmployeeShifts = getShiftsForEmployeeAndDate(employeeId, date);

    const newStart = parseLocalDateTime(newStartTime);
    const newEnd = parseLocalDateTime(newEndTime);

    return allEmployeeShifts.some(existingShift => {
      if (excludeShiftId && existingShift.id === excludeShiftId) {
        return false;
      }

      const existingStart = parseLocalDateTime(existingShift.startTime);
      const existingEnd = parseLocalDateTime(existingShift.endTime);

      return (newStart < existingEnd && newEnd > existingStart);
    });
  };

  const markScheduleChangedInBackground = useCallback(() => {
    if (publicationStatus?.status !== 'Published' || !selectedLocationId || !selectedScheduleId) return;
    const weekStartStrForStatus = format(weekStart, 'yyyy-MM-dd');
    void (async () => {
      try {
        await schedulePublicationStatusApi.markChanged(selectedLocationId, selectedScheduleId, weekStartStrForStatus);
        const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStrForStatus);
        setPublicationStatus(updatedStatus);
      } catch (error) {
        console.error('Error marking schedule as changed:', error);
      }
    })();
  }, [publicationStatus?.status, selectedLocationId, selectedScheduleId, weekStart]);

  const confirmExpectedCountsForCreate = useCallback(async (payload: CreateShiftRequest) => {
    const shiftDate = payload.startTime.slice(0, 10);
    const result = evaluateShiftCreateExpectedCounts({
      week: weekExpectedCounts,
      shifts: shiftsRef.current,
      scheduleId: Number(payload.scheduleId),
      locationId: Number(payload.locationId),
      shiftDate,
    });
    if (!result) return true;
    const scheduleName =
      schedules.find((s) => s.id === Number(payload.scheduleId))?.name ?? 'this schedule';
    const message = [
      `Creating this shift on ${formatCompanyDate(shiftDate)} would go over the expected counts for ${scheduleName}.`,
      ...result.messages,
      'Create the shift anyway?',
    ].join('\n\n');
    return appConfirm(message, {
      title: 'Expected counts',
      confirmLabel: 'Create anyway',
      cancelLabel: 'Cancel',
    });
  }, [weekExpectedCounts, schedules]);

  const persistShiftCreateInBackground = useCallback((tempId: number, payload: CreateShiftRequest, optimisticShift: Shift) => {
    setShifts((prev) => {
      const employeeShiftCount = countEmployeeScheduleWeekShifts(
        prev,
        payload.scheduleId,
        payload.locationId,
        assigneeRowId(payload),
        weekStart,
        weekEnd
      );
      setScheduleCounts((countsPrev) => {
        const current = countsPrev[payload.scheduleId] ?? { totalShifts: 0, uniqueEmployees: 0, totalEmployeesAssigned: 0 };
        return {
          ...countsPrev,
          [payload.scheduleId]: {
            ...current,
            totalShifts: current.totalShifts + 1,
            uniqueEmployees: current.uniqueEmployees + (employeeShiftCount === 0 ? 1 : 0),
          },
        };
      });
      const next = [...prev, optimisticShift];
      shiftsRef.current = next;
      return next;
    });
    markScheduleChangedInBackground();
    void (async () => {
      try {
        const createdRaw = await shiftApi.create(payload);
        const warnings = extractRestRuleWarnings(createdRaw);
        const created = normalizeShift({
          ...createdRaw,
          ...(warnings ? { restRuleWarnings: warnings } : {}),
        });
        if (warnings?.length) {
          setRestRuleWarningModal({ warnings, shiftId: created.id });
        }
        setShifts((prev) => {
          const next = [...prev.filter((s) => s.id !== tempId && s.id !== created.id), created];
          shiftsRef.current = next;
          return next;
        });
        refreshScheduleCountsInBackground();
      } catch (error) {
        console.error('Error creating shift:', error);
        setShifts((prev) => {
          const next = prev.filter((s) => s.id !== tempId);
          shiftsRef.current = next;
          return next;
        });
        bumpScheduleCountsForRemove(optimisticShift);
        refreshScheduleCountsInBackground();
        alert('Failed to create shift. Please try again.');
      }
    })();
  }, [markScheduleChangedInBackground, bumpScheduleCountsForRemove, refreshScheduleCountsInBackground, weekStart, weekEnd]);

  const createShiftOptimistically = useCallback(async (payload: CreateShiftRequest) => {
    if (!(await confirmExpectedCountsForCreate(payload))) return;
    const tempId = allocateTempShiftId();
    persistShiftCreateInBackground(tempId, payload, buildOptimisticShift(tempId, payload));
  }, [confirmExpectedCountsForCreate, persistShiftCreateInBackground]);

  // Calculate total hours for a specific day (across all employees) - CURRENT SCHEDULE ONLY
  const getDayTotalHours = (date: Date): number => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEmployees.reduce((total, employee) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employee.id, date);
      // Filter to only current schedule shifts
      const currentScheduleShifts = employeeShifts.filter(isCurrentScheduleShift);
      const employeeDayTotal = currentScheduleShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
      return total + employeeDayTotal;
    }, 0);
  };

  // Calculate total hours for a specific day across ALL schedules (same location)
  const getDayTotalHoursAllSchedules = (date: Date): number => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEmployees.reduce((total, employee) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employee.id, date);
      // Filter to only shifts from selected location (all schedules)
      const locationShifts = employeeShifts.filter(isSameLocationShift);
      const employeeDayTotal = locationShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
      return total + employeeDayTotal;
    }, 0);
  };

  // Calculate total hours for an employee (across all days in the week) - CURRENT SCHEDULE ONLY
  const getEmployeeTotalHours = (employeeId: number): number => {
    return calendarWeekDays.reduce((total, day) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      // Filter to only current schedule shifts
      const currentScheduleShifts = employeeShifts.filter(isCurrentScheduleShift);
      const dayTotal = currentScheduleShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
      return total + dayTotal;
    }, 0);
  };

  // Calculate total hours for an employee across ALL schedules (same location only)
  const getEmployeeTotalHoursAllSchedules = (employeeId: number): number => {
    return calendarWeekDays.reduce((total, day) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      // Filter to only shifts from selected location (all schedules)
      const locationShifts = employeeShifts.filter(isSameLocationShift);
      const dayTotal = locationShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
      return total + dayTotal;
    }, 0);
  };

  // Calculate grand total (all employees, all days) - CURRENT SCHEDULE ONLY
  const getGrandTotalHours = (): number => {
    return filteredEmployees.reduce((total, employee) => {
      return total + getEmployeeTotalHours(employee.id);
    }, 0);
  };

  // Calculate grand total hours across ALL schedules (same location)
  const getGrandTotalHoursAllSchedules = (): number => {
    return filteredEmployees.reduce((total, employee) => {
      return total + getEmployeeTotalHoursAllSchedules(employee.id);
    }, 0);
  };

  // Calculate labor cost for a shift (uses shared util with employee pay records map)
  const getShiftLaborCostLocal = (shift: Shift): number =>
    getShiftLaborCost(shift, employeePayRecords);

  // Calculate total cost for a specific day (across all employees) - CURRENT SCHEDULE ONLY
  const getDayTotalCost = (date: Date): number => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEmployees.reduce((total, employee) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employee.id, date);
      // Filter to only current schedule shifts
      const currentScheduleShifts = employeeShifts.filter(isCurrentScheduleShift);
      const employeeDayTotal = currentScheduleShifts.reduce((sum, shift) => sum + getShiftLaborCostLocal(shift), 0);
      return total + employeeDayTotal;
    }, 0);
  };

  // Calculate total cost for a specific day across ALL schedules (same location)
  const getDayTotalCostAllSchedules = (date: Date): number => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEmployees.reduce((total, employee) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employee.id, date);
      // Filter to only shifts from selected location (all schedules)
      const locationShifts = employeeShifts.filter(isSameLocationShift);
      const employeeDayTotal = locationShifts.reduce((sum, shift) => sum + getShiftLaborCostLocal(shift), 0);
      return total + employeeDayTotal;
    }, 0);
  };

  // Calculate total cost for an employee (across all days in the week) - CURRENT SCHEDULE ONLY
  const getEmployeeTotalCost = (employeeId: number): number => {
    return calendarWeekDays.reduce((total, day) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      // Filter to only current schedule shifts
      const currentScheduleShifts = employeeShifts.filter(isCurrentScheduleShift);
      const dayTotal = currentScheduleShifts.reduce((sum, shift) => sum + getShiftLaborCostLocal(shift), 0);
      return total + dayTotal;
    }, 0);
  };

  // Calculate total cost for an employee across ALL schedules (same location only)
  const getEmployeeTotalCostAllSchedules = (employeeId: number): number => {
    return calendarWeekDays.reduce((total, day) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      // Filter to only shifts from selected location (all schedules)
      const locationShifts = employeeShifts.filter(isSameLocationShift);
      const dayTotal = locationShifts.reduce((sum, shift) => sum + getShiftLaborCostLocal(shift), 0);
      return total + dayTotal;
    }, 0);
  };

  // Calculate grand total cost (all employees, all days) - CURRENT SCHEDULE ONLY
  const getGrandTotalCost = (): number => {
    return filteredEmployees.reduce((total, employee) => {
      return total + getEmployeeTotalCost(employee.id);
    }, 0);
  };

  // Calculate grand total cost across ALL schedules (same location)
  const getGrandTotalCostAllSchedules = (): number => {
    return filteredEmployees.reduce((total, employee) => {
      return total + getEmployeeTotalCostAllSchedules(employee.id);
    }, 0);
  };

  // Memoized totals — skip expensive recompute while week-view is loading (keep previous values).
  const totalsCache = useMemo(() => {
    if (loadingWeekView && totalsCacheRef.current) {
      return totalsCacheRef.current;
    }

    const locId = Number(selectedLocationId);
    const schedId = Number(selectedScheduleId);
    const employeeHoursCurrent: Record<number, number> = {};
    const employeeCostCurrent: Record<number, number> = {};
    const employeeHoursAll: Record<number, number> = {};
    const employeeCostAll: Record<number, number> = {};
    const dayHoursCurrent: Record<string, number> = {};
    const dayCostCurrent: Record<string, number> = {};
    const dayHoursAll: Record<string, number> = {};
    const dayCostAll: Record<string, number> = {};

    for (const day of calendarWeekDays) {
      const dayStr = format(day, 'yyyy-MM-dd');
      dayHoursCurrent[dayStr] = 0;
      dayCostCurrent[dayStr] = 0;
      dayHoursAll[dayStr] = 0;
      dayCostAll[dayStr] = 0;
    }

    let grandHoursCurrent = 0;
    let grandCostCurrent = 0;
    let grandHoursAll = 0;
    let grandCostAll = 0;

    for (const emp of filteredEmployees) {
      let empHoursCurrent = 0;
      let empCostCurrent = 0;
      let empHoursAll = 0;
      let empCostAll = 0;
      const dateMap = shiftsByEmployeeDate.get(emp.id);

      for (const day of calendarWeekDays) {
        const dayStr = format(day, 'yyyy-MM-dd');
        const dayShifts = dateMap?.get(dayStr) ?? [];

        for (const shift of dayShifts) {
          const shiftHours = getShiftTotalHours(shift);
          const isLoc = Number(shift.locationId) === locId;
          const isSched = isLoc && Number(shift.scheduleId) === schedId;

          if (isSched) {
            const cost = getShiftLaborCost(shift, employeePayRecords);
            empHoursCurrent += shiftHours;
            empCostCurrent += cost;
            dayHoursCurrent[dayStr] += shiftHours;
            dayCostCurrent[dayStr] += cost;
          }
          if (isLoc) {
            const cost = getShiftLaborCost(shift, employeePayRecords);
            empHoursAll += shiftHours;
            empCostAll += cost;
            dayHoursAll[dayStr] += shiftHours;
            dayCostAll[dayStr] += cost;
          }
        }
      }

      employeeHoursCurrent[emp.id] = empHoursCurrent;
      employeeCostCurrent[emp.id] = empCostCurrent;
      employeeHoursAll[emp.id] = empHoursAll;
      employeeCostAll[emp.id] = empCostAll;
      grandHoursCurrent += empHoursCurrent;
      grandCostCurrent += empCostCurrent;
      grandHoursAll += empHoursAll;
      grandCostAll += empCostAll;
    }

    const result = {
      employeeHoursCurrent,
      employeeCostCurrent,
      employeeHoursAll,
      employeeCostAll,
      dayHoursCurrent,
      dayCostCurrent,
      dayHoursAll,
      dayCostAll,
      grandHoursCurrent,
      grandCostCurrent,
      grandHoursAll,
      grandCostAll,
    };
    totalsCacheRef.current = result;
    return result;
  }, [
    shiftsByEmployeeDate,
    employeePayRecords,
    calendarWeekDays,
    filteredEmployees,
    selectedScheduleId,
    selectedLocationId,
    loadingWeekView,
  ]);

  // Get formula explanation for PayRecord
  const getFormulaExplanation = (payRecord: PayRecord): { formula: string; description: string; values: Record<string, any> } => {
    const paymentTypeName = payRecord.paymentType?.name || '';
    const baseRate = payRecord.baseRate || 0;
    const periodGross = baseRate;
    const assignLaborCostByName = payRecord.assignLaborCostBy?.name || '';
    const contractedHours = payRecord.contractedHours || 0;
    const workingDaysPerWeek = payRecord.workingDaysPerWeek || 0;
    const dailyRate = payRecord.dailyRate || 0;
    const presetDaysCount = payRecord.payRecordWorkingDays?.length || 0;
    const paymentFrequencyName = payRecord.paymentFrequency?.name || '';
    const isMonthlyFrequency = paymentFrequencyName === 'Monthly';

    if (paymentTypeName === 'Hourly') {
      return {
        formula: 'Hourly Rate = Base Rate',
        description: 'For Hourly payment type, Base Rate is already the hourly rate.',
        values: {
          'Base Rate': `€${baseRate.toFixed(2)}`,
          'Hourly Rate': `€${baseRate.toFixed(2)}`,
        }
      };
    }
    
    // For non-hourly types, MUST use AssignLaborCostBy option
    if (!assignLaborCostByName) {
      return {
        formula: 'Cost Allocation Not Set',
        description: 'For non-hourly payment types, Assign Labor Cost By option must be set to calculate hourly rate.',
        values: {
          'Payment Type': paymentTypeName,
          'Assign Labor Cost By': 'Not Set',
        }
      };
    }
    
    if (assignLaborCostByName === 'Allocated Hours' && contractedHours > 0) {
      let hourlyRate: number;
      let formula: string;
      let description: string;
      let values: Record<string, any>;
      
      if (isMonthlyFrequency) {
        const monthlyHours = contractedHours * 4.33;
        hourlyRate = monthlyHours > 0 && periodGross > 0 ? periodGross / monthlyHours : 0;
        formula = 'Hourly Rate = Pay amount ÷ (Contracted Hours per Week × 4.33 weeks per month)';
        description =
          'Pay amount is the monthly gross for this frequency. Convert to monthly hours then divide.';
        values = {
          'Monthly gross (pay amount)': `€${periodGross.toFixed(2)}`,
          'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
          'Weeks per Month': '4.33',
          'Monthly Hours': `${monthlyHours.toFixed(2)}`,
          'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
        };
      } else {
        hourlyRate = periodGross > 0 ? periodGross / contractedHours : 0;
        formula = 'Hourly Rate = Pay amount ÷ Contracted Hours per Week';
        description = 'Pay amount is the weekly (or period) gross. Divide by contracted hours per week.';
        values = {
          'Pay amount': `€${periodGross.toFixed(2)}`,
          'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
          'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
        };
      }
      
      return { formula, description, values };
    } else if (assignLaborCostByName === 'Daily Rate' && workingDaysPerWeek > 0) {
      if (dailyRate > 0 && contractedHours > 0) {
        const weeklyTotal = dailyRate * workingDaysPerWeek;
        const hourlyRate = weeklyTotal / contractedHours;
        return {
          formula: 'Hourly Rate = (Daily Rate × Working Days per Week) ÷ Contracted Hours per Week',
          description: 'Calculate weekly total from daily rate, then divide by contracted hours.',
          values: {
            'Daily Rate': `€${dailyRate.toFixed(2)}`,
            'Working Days per Week': `${workingDaysPerWeek}`,
            'Weekly Total': `€${weeklyTotal.toFixed(2)}`,
            'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
            'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
          }
        };
      } else {
        // Daily Rate option selected but dailyRate not set
        return {
          formula: 'Daily Rate Not Set',
          description: 'Assign Labor Cost By is set to "Daily Rate" but Daily Rate value is not provided.',
          values: {
            'Assign Labor Cost By': 'Daily Rate',
            'Daily Rate': 'Not Set',
          }
        };
      }
    } else if (assignLaborCostByName === 'Pre Set Days' && presetDaysCount > 0 && contractedHours > 0) {
      let hourlyRate: number;
      let formula: string;
      let description: string;
      let values: Record<string, any>;
      
      if (isMonthlyFrequency) {
        const monthlyHours = contractedHours * 4.33 * (presetDaysCount / 5);
        hourlyRate = monthlyHours > 0 && periodGross > 0 ? periodGross / monthlyHours : 0;
        formula = 'Hourly Rate = Pay amount ÷ (Contracted Hours per Week × 4.33 × Preset Days ÷ 5)';
        description =
          'Pay amount is monthly gross. Hours adjusted for preset days ratio (5-day week).';
        values = {
          'Pay amount (monthly gross)': `€${periodGross.toFixed(2)}`,
          'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
          'Preset Days Count': `${presetDaysCount}`,
          'Weeks per Month': '4.33',
          'Adjusted Monthly Hours': `${monthlyHours.toFixed(2)}`,
          'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
        };
      } else {
        const adjustedHours = contractedHours * (presetDaysCount / 5);
        hourlyRate = adjustedHours > 0 && periodGross > 0 ? periodGross / adjustedHours : 0;
        formula = 'Hourly Rate = Pay amount ÷ (Contracted Hours × Preset Days ÷ 5)';
        description = 'Pay amount is period gross. Adjust contracted hours for preset days ratio.';
        values = {
          'Pay amount': `€${periodGross.toFixed(2)}`,
          'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
          'Preset Days Count': `${presetDaysCount}`,
          'Adjusted Hours': `${adjustedHours.toFixed(2)}`,
          'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
        };
      }
      
      return { formula, description, values };
    } else if (assignLaborCostByName === 'Do not Allocate') {
      return {
        formula: 'No Cost Allocation',
        description: 'Assign Labor Cost By is set to "Do not Allocate". Labor cost will not be calculated.',
        values: {
          'Assign Labor Cost By': 'Do not Allocate',
        }
      };
    }
    
    // Unknown or unsupported AssignLaborCostBy option, or required fields missing
    return {
      formula: 'Cannot Calculate Hourly Rate',
      description: `Cannot calculate hourly rate. Check that Assign Labor Cost By is properly configured and all required fields are set.`,
      values: {
        'Assign Labor Cost By': assignLaborCostByName || 'Not Set',
        'Contracted Hours': contractedHours > 0 ? `${contractedHours.toFixed(2)}` : 'Not Set',
      }
    };
  };

  // Get shift breakdown for an employee
  const getEmployeeShiftBreakdown = (employeeId: number): Array<{
    shift: Shift;
    date: Date;
    shiftHours: number;
    breakMins: number;
    breakPaid: boolean;
    hourlyRate: number;
    cost: number;
  }> => {
    const payRecord = employeePayRecords[employeeId];
    if (!payRecord) return [];

    const hourlyRate = calculateHourlyRateFromPayRecord(payRecord);
    const breakdown: Array<{
      shift: Shift;
      date: Date;
      shiftHours: number;
      breakMins: number;
      breakPaid: boolean;
      hourlyRate: number;
      cost: number;
    }> = [];

    calendarWeekDays.forEach(day => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      employeeShifts.forEach(shift => {
        // Use paid hours for cost calculation (deducts unpaid break if breakTimePaid is false)
        const shiftHours = getShiftPaidHoursForCost(shift);
        const breakMins = shift.unpaidHours ? Math.round(Number(shift.unpaidHours) * 60) : 0;
        const breakPaid = shift.breakTimePaid ?? false;
        const cost = hourlyRate * shiftHours;
        breakdown.push({
          shift,
          date: day,
          shiftHours,
          breakMins,
          breakPaid,
          hourlyRate,
          cost
        });
      });
    });

    return breakdown;
  };

  // Drag handlers - setData is required in dragstart for drop to fire in most browsers
  const handleDragStart = (e: React.DragEvent, template: ShiftTemplate) => {
    setDraggedTemplate(template);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `template:${template.id}`);
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'shift-template', id: template.id }));
  };

  const handleDragOver = (e: React.DragEvent, employeeId: number, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell(`${employeeId}-${date.toISOString()}`);
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = (e: React.DragEvent, employeeId: number, date: Date) => {
    e.preventDefault();
    setDragOverCell(null);

    if (isReadOnly) return;
    if (!selectedLocationId || !selectedScheduleId) return;
    if (hasApprovedHolidayOnDate(employeeId, date)) {
      alert('Cannot create shift: Employee has approved holiday on this day.');
      setDraggedTemplate(null);
      return;
    }
    if (hasApprovedAllDayUnavailability(employeeId, date)) {
      alert('Cannot create shift: Employee has approved unavailability on this day.');
      setDraggedTemplate(null);
      return;
    }
    
    // Prefer React state, but fall back to dataTransfer in case state wasn't set
    let template = draggedTemplate;
    if (!template) {
      try {
        const json = e.dataTransfer.getData('application/json');
        if (json) {
          const parsed = JSON.parse(json);
          if (parsed?.type === 'shift-template' && parsed.id) {
            template = sortedShiftTemplates.find(t => t.id === parsed.id) || null;
          }
        }
      } catch {
        // Ignore parse errors and fall back to no template
      }
      if (!template) {
        const text = e.dataTransfer.getData('text/plain');
        if (text?.startsWith('template:')) {
          const id = Number(text.split(':')[1]);
          if (!Number.isNaN(id)) {
            template = sortedShiftTemplates.find(t => t.id === id) || null;
          }
        }
      }
    }
    if (!template) return;
    const t: ShiftTemplate = template;
    setDraggedTemplate(null);

    const executeDrop = () => {
      const { startTime, endTime: finalEndTime } = buildShiftTimesForDate(date, t.startTime, t.endTime);

      if (checkTimeOverlap(employeeId, date, startTime, finalEndTime)) {
        alert('Cannot create shift: Time overlaps with an existing shift in this cell.');
        return;
      }

      const employee = employees.find(emp => emp.id === employeeId);
      if (!employee?.isPlaceholder) {
        const unavailable = approvedUnavailabilityBlocksShift(
          getUnavailabilityFor(employeeId, date),
          parseLocalDateTime(startTime),
          parseLocalDateTime(finalEndTime)
        );
        if (unavailable) {
          alert(unavailable);
          return;
        }
      }
      const defaultPositionId = employee?.profile?.mainPositionId;
      const shiftColor = colorMode === 'position' && defaultPositionId 
        ? getShiftColorForMode(defaultPositionId)
        : t.color;

      createShiftOptimistically({
        ...assigneeFromGridRow(employeeId),
        locationId: selectedLocationId,
        scheduleId: selectedScheduleId,
        startTime,
        endTime: finalEndTime,
        color: shiftColor,
        displayOption: t.displayOption,
        unpaidHours: t.breakUseGlobalSettings === false ? (t.unpaidBreakMins || 0) / 60 : 0,
        breakTimePaid: t.breakTimePaid ?? false,
        breakUseGlobalSettings: t.breakUseGlobalSettings !== false,
        positionId: defaultPositionId,
      });
    };

    checkPublishedAndWarn(executeDrop);
  };

  // Calculate hours from start and end time
  const calculateHours = (startTime: string, endTime: string, breakMins: number = 0): number => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;
    const diffMins = endTotal > startTotal ? endTotal - startTotal : (24 * 60 - startTotal) + endTotal;
    return (diffMins - breakMins) / 60;
  };

  // Calculate default break minutes based on shift hours and break time rules
  const calculateDefaultBreakMinutes = (startTime: string, endTime: string): number => {
    if (!startTime || !endTime) return 0;
    
    // Calculate total hours without break
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;
    const diffMins = endTotal > startTotal ? endTotal - startTotal : (24 * 60 - startTotal) + endTotal;
    const totalHours = diffMins / 60;
    
    // Find matching break time rule
    if (breakTimeRules && breakTimeRules.length > 0) {
      const matchingRule = breakTimeRules.find(rule => 
        totalHours >= rule.minHours && totalHours < rule.maxHours
      );
      
      if (matchingRule) {
        return matchingRule.breakMinutes;
      }
    }
    
    // Default to 0 if no matching rule found
    return 0;
  };

  const handleCreateShift = (employeeId: number, date: Date) => {
    if (isReadOnly) return;
    if (hasApprovedHolidayOnDate(employeeId, date)) {
      return;
    }
    if (hasApprovedAllDayUnavailability(employeeId, date)) {
      return;
    }

    if (!checkPublishedAndWarn(() => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const employee = employees.find(emp => emp.id === employeeId);
      const defaultPositionId = employee?.profile?.mainPositionId;
      const defaultColor = getShiftColorForMode(defaultPositionId);
      const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
      
      setBreakMinsManuallySet(false);
      setFormData({
        name: '',
        startTime: '',
        endTime: '',
        hours: 0,
        unpaidBreakMins: 0,
        breakTimePaid: defaultBreakTimePaid,
        breakUseGlobalSettings: true,
        totalHours: 0,
        color: defaultColor,
        displayOption: 'time',
        positionId: defaultPositionId,
        shiftGroupId: undefined,
        notes: '',
        isManualColorOverride: false,
        originalColor: defaultColor,
        isPayExtra: false,
      });
      setEditingShift({
        id: 0,
        employeeId,
        locationId: selectedLocationId!,
        scheduleId: selectedScheduleId!,
        startTime: `${dateStr}T00:00:00`,
        endTime: `${dateStr}T00:00:00`,
        color: defaultColor,
        displayOption: 'time',
      } as Shift);
      setShowShiftForm(true);
    })) {
      return;
    }
    
    const dateStr = format(date, 'yyyy-MM-dd');
    const employee = employees.find(emp => emp.id === employeeId);
    const defaultPositionId = employee?.profile?.mainPositionId;
    const defaultColor = '#3b82f6';
    const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
    
    setBreakMinsManuallySet(false);
    setFormData({
      name: '',
      startTime: '',
      endTime: '',
      hours: 0,
      unpaidBreakMins: 0,
      breakTimePaid: defaultBreakTimePaid,
      breakUseGlobalSettings: true,
      totalHours: 0,
      color: defaultColor,
      displayOption: 'time',
      positionId: defaultPositionId,
      shiftGroupId: undefined,
      notes: '',
      isManualColorOverride: false,
      originalColor: defaultColor,
      isPayExtra: false,
    });
    setEditingShift({
      id: 0,
      employeeId,
      locationId: selectedLocationId!,
      scheduleId: selectedScheduleId!,
      startTime: `${dateStr}T00:00:00`,
      endTime: `${dateStr}T00:00:00`,
      color: defaultColor,
      displayOption: 'time',
    } as Shift);
    setShowShiftForm(true);
  };

  const handleEditShift = (shift: Shift) => {
    if (isReadOnly) return;

    // Only allow editing shifts from the current schedule and location
    if (!isCurrentScheduleShift(shift)) {
      return; // Don't allow editing cross-schedule or cross-location shifts
    }

    if (marketplaceByShiftId.has(shift.id)) {
      return;
    }

    const shiftDate = parseLocalDateTime(shift.startTime);
    if (hasApprovedHolidayOnDate(shift.employeeId, shiftDate)) {
      return;
    }
    if (hasApprovedAllDayUnavailability(shift.employeeId, shiftDate)) {
      return;
    }
    
    if (!checkPublishedAndWarn(() => {
      setEditingShift(shift);
      setBreakMinsManuallySet(true); // When editing, break minutes are already set
      const startTime = format(parseLocalDateTime(shift.startTime), 'HH:mm');
      const endTime = format(parseLocalDateTime(shift.endTime), 'HH:mm');
      const useGlobalBreak = shift.breakUseGlobalSettings !== false;
      const unpaidBreakMins = useGlobalBreak ? 0 : (shift.unpaidHours ? Math.round(Number(shift.unpaidHours) * 60) : 0);
      const hours = calculateHours(startTime, endTime, unpaidBreakMins);
      const totalHours = useGlobalBreak
        ? calculateHours(startTime, endTime, 0)
        : (shift.paidHours ? Number(shift.paidHours) : hours);
      
      setFormData({
        name: '',
        startTime,
        endTime,
        hours,
        unpaidBreakMins,
        breakTimePaid: shift.breakTimePaid ?? (breakTimeSettings?.isPaid ?? false),
        breakUseGlobalSettings: useGlobalBreak,
        totalHours,
        color: shift.color || '#3b82f6',
        displayOption: (shift.displayOption as 'time' | 'f' | 'close') || 'time',
        positionId: shift.positionId || (() => {
          const emp = employees.find(e => e.id === shift.employeeId);
          return emp?.profile?.mainPositionId;
        })(),
        shiftGroupId: shift.shiftGroupId ?? undefined,
        notes: shift.notes || '',
        isManualColorOverride: shift.isManualColorOverride || false,
        originalColor: shift.color || '#3b82f6',
        isPayExtra: shift.isPayExtra === true,
      });
      setShowShiftForm(true);
    })) {
      return;
    }
    
    setEditingShift(shift);
    setBreakMinsManuallySet(true); // When editing, break minutes are already set
    const startTime = format(parseLocalDateTime(shift.startTime), 'HH:mm');
    const endTime = format(parseLocalDateTime(shift.endTime), 'HH:mm');
    const useGlobalBreak = shift.breakUseGlobalSettings !== false;
    const unpaidBreakMins = useGlobalBreak ? 0 : (shift.unpaidHours ? Math.round(Number(shift.unpaidHours) * 60) : 0);
    const hours = calculateHours(startTime, endTime, unpaidBreakMins);
    const totalHours = useGlobalBreak
      ? calculateHours(startTime, endTime, 0)
      : (shift.paidHours ? Number(shift.paidHours) : hours);
    
    setFormData({
      name: '',
      startTime,
      endTime,
      hours,
      unpaidBreakMins,
      breakTimePaid: shift.breakTimePaid ?? (breakTimeSettings?.isPaid ?? false),
      breakUseGlobalSettings: useGlobalBreak,
      totalHours,
      color: shift.color || '#3b82f6',
      displayOption: (shift.displayOption as 'time' | 'f' | 'close') || 'time',
      positionId: shift.positionId || (() => {
        const emp = employees.find(e => e.id === shift.employeeId);
        return emp?.profile?.mainPositionId;
      })(),
      shiftGroupId: shift.shiftGroupId ?? undefined,
      notes: shift.notes || '',
      isManualColorOverride: shift.isManualColorOverride || false,
      originalColor: shift.color || '#3b82f6',
      isPayExtra: shift.isPayExtra === true,
    });
    setShowShiftForm(true);
  };

  const handleSaveShift = async () => {
    if (isReadOnly) return;
    if (!editingShift) return;
    if (editingShift.id !== 0 && marketplaceByShiftId.has(editingShift.id)) return;
    
    // Prevent multiple simultaneous saves
    if (savingShift) return;
    
    setSavingShift(true);

    try {
      const dateStr = format(parseLocalDateTime(editingShift.startTime), 'yyyy-MM-dd');
      const startTime = `${dateStr}T${formData.startTime}:00`;
      const endTime = `${dateStr}T${formData.endTime}:00`;
      const unpaidHours = formData.breakUseGlobalSettings ? 0 : formData.unpaidBreakMins / 60;
      const effectiveBreakMins = formData.breakUseGlobalSettings ? 0 : formData.unpaidBreakMins;
      const recalculatedHours = calculateHours(formData.startTime, formData.endTime, effectiveBreakMins);

      // Get the date for overlap checking
      const shiftDate = parseLocalDateTime(editingShift.startTime);
      
      // Check for time overlaps (exclude current shift if editing)
      const excludeShiftId = editingShift.id !== 0 ? editingShift.id : undefined;
      const hasOverlap = checkTimeOverlap(editingShift.employeeId, shiftDate, startTime, endTime, excludeShiftId);
      if (hasOverlap) {
        alert('Cannot save shift: Time overlaps with an existing shift in this cell.');
        setSavingShift(false);
        return;
      }

      const unavailable = approvedUnavailabilityBlocksShift(
        getUnavailabilityFor(editingShift.employeeId, shiftDate),
        parseLocalDateTime(startTime),
        parseLocalDateTime(endTime)
      );
      if (unavailable) {
        alert(unavailable);
        setSavingShift(false);
        return;
      }

      // Preserve stored/template color in shift color mode; derive from position only in position color mode.
      const shiftColor = formData.isManualColorOverride
        ? formData.color
        : colorMode === 'position'
          ? getShiftColorForMode(formData.positionId)
          : (formData.color || editingShift.color || '#3b82f6');

      const shiftEmployee = employees.find((e) => e.id === editingShift.employeeId)
        ?? locationEmployees.find((e) => e.id === editingShift.employeeId);
      const allowPayExtra = shiftEmployee?.payroll?.allowAdditiveHourlyPay === true;
      const payExtra = allowPayExtra && formData.isPayExtra;
      if (
        payExtra &&
        formData.positionId != null &&
        shiftEmployee?.profile?.mainPositionId != null &&
        formData.positionId === shiftEmployee.profile.mainPositionId
      ) {
        const ok = await appConfirm(
          'Pay Extra is on with the employee’s main position. Additive matrix pay will apply on top of salary for these hours. Continue?'
        );
        if (!ok) {
          setSavingShift(false);
          return;
        }
      }
      
      if (editingShift.id === 0) {
        const payload: CreateShiftRequest = {
          ...assigneeFromGridRow(editingShift.employeeId),
          locationId: editingShift.locationId,
          scheduleId: editingShift.scheduleId,
          startTime,
          endTime,
          unpaidHours,
          breakTimePaid: formData.breakTimePaid,
          breakUseGlobalSettings: formData.breakUseGlobalSettings,
          color: shiftColor,
          displayOption: formData.displayOption,
          positionId: formData.positionId,
          shiftGroupId: formData.shiftGroupId,
          notes: formData.notes,
          isManualColorOverride: formData.isManualColorOverride,
          isPayExtra: payExtra,
        };
        if (!(await confirmExpectedCountsForCreate(payload))) {
          setSavingShift(false);
          return;
        }
        const tempId = allocateTempShiftId();
        const optimisticShift = buildOptimisticShift(tempId, payload);
        setShifts((prev) => {
          const employeeShiftCount = countEmployeeScheduleWeekShifts(
            prev,
            payload.scheduleId,
            payload.locationId,
            assigneeRowId(payload),
            weekStart,
            weekEnd
          );
          setScheduleCounts((countsPrev) => {
            const current = countsPrev[payload.scheduleId] ?? { totalShifts: 0, uniqueEmployees: 0, totalEmployeesAssigned: 0 };
            return {
              ...countsPrev,
              [payload.scheduleId]: {
                ...current,
                totalShifts: current.totalShifts + 1,
                uniqueEmployees: current.uniqueEmployees + (employeeShiftCount === 0 ? 1 : 0),
              },
            };
          });
          const next = [...prev.filter((s) => s.id !== tempId), optimisticShift];
          shiftsRef.current = next;
          return next;
        });
        setShowShiftForm(false);
        setEditingShift(null);
        setBreakMinsManuallySet(false);
        setHasUnsavedChanges(false);
        setShowPositionDropdown(false);
        markScheduleChangedInBackground();
        void (async () => {
          try {
            const createdRaw = await shiftApi.create(payload);
            const warnings = extractRestRuleWarnings(createdRaw);
            const created = normalizeShift({
              ...createdRaw,
              ...(warnings ? { restRuleWarnings: warnings } : {}),
            });
            if (warnings?.length) {
              setRestRuleWarningModal({ warnings, shiftId: created.id });
            }
            setShifts((prev) => {
              const next = [...prev.filter((s) => s.id !== tempId && s.id !== created.id), created];
              shiftsRef.current = next;
              return next;
            });
            refreshScheduleCountsInBackground();
          } catch (error) {
            console.error('Error saving shift:', error);
            setShifts((prev) => {
              const next = prev.filter((s) => s.id !== tempId);
              shiftsRef.current = next;
              return next;
            });
            bumpScheduleCountsForRemove(optimisticShift);
            refreshScheduleCountsInBackground();
            alert('Failed to save shift. Please try again.');
          }
        })();
      } else {
        const updatePayload = {
          startTime,
          endTime,
          unpaidHours,
          breakTimePaid: formData.breakTimePaid,
          breakUseGlobalSettings: formData.breakUseGlobalSettings,
          color: shiftColor,
          displayOption: formData.displayOption,
          positionId: formData.positionId,
          shiftGroupId: formData.shiftGroupId,
          clearShiftGroup: !formData.shiftGroupId,
          notes: formData.notes,
          isManualColorOverride: formData.isManualColorOverride,
          isPayExtra: payExtra,
        };
        const previousShift = editingShift;
        const optimisticUpdated = normalizeShift({ ...previousShift, ...updatePayload });
        setShifts((prev) => prev.map((s) => (s.id === optimisticUpdated.id ? optimisticUpdated : s)));
        setShowShiftForm(false);
        setEditingShift(null);
        setBreakMinsManuallySet(false);
        setHasUnsavedChanges(false);
        setShowPositionDropdown(false);
        markScheduleChangedInBackground();
        void (async () => {
          try {
            const updateResult = await shiftApi.update(previousShift.id, updatePayload);
            const warnings = extractRestRuleWarnings(updateResult);
            const updated = normalizeShift({
              ...(await shiftApi.getById(previousShift.id)),
              ...(warnings ? { restRuleWarnings: warnings } : { restRuleWarnings: undefined }),
            });
            if (warnings?.length) {
              setRestRuleWarningModal({ warnings, shiftId: updated.id });
            }
            setShifts((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
          } catch (error) {
            console.error('Error saving shift:', error);
            setShifts((prev) => prev.map((s) => (s.id === previousShift.id ? previousShift : s)));
            alert('Failed to save shift. Please try again.');
          }
        })();
      }
    } catch (error) {
      console.error('Error saving shift:', error);
      alert('Failed to save shift. Please try again.');
    } finally {
      setSavingShift(false);
    }
  };

  // Get available positions for an employee (main + sub positions)
  const getEmployeePositions = (employeeId: number): Position[] => {
    const employee = employees.find(emp => emp.id === employeeId);
    if (!employee?.profile) return [];
    
    const mainPositionId = employee.profile.mainPositionId;
    const subPositionIds = employee.profile.subPositionIds || [];
    const allPositionIds = [mainPositionId, ...subPositionIds].filter((id): id is number => id !== undefined && id !== null);
    
    return positions.filter(pos => allPositionIds.includes(pos.id));
  };

  // Get the color for a shift based on the color mode setting
  const getShiftDisplayColor = (shift: Shift): string => {
    if (colorMode === 'position' && shift.positionId) {
      const position = positions.find(p => p.id === shift.positionId);
      return position?.color || shift.color || '#3b82f6';
    }
    return shift.color || '#3b82f6';
  };

  // Get the color to use when creating/editing a shift based on color mode
  const getShiftColorForMode = (positionId?: number): string => {
    if (colorMode === 'position' && positionId) {
      const position = positions.find(p => p.id === positionId);
      return position?.color || '#3b82f6';
    }
    return '#3b82f6'; // Default shift color
  };

  const handleDeleteShift = (id: number) => {
    if (isReadOnly) return;
    if (marketplaceByShiftId.has(id)) return;

    const shiftToDelete = shiftsRef.current.find((s) => s.id === id);
    if (!shiftToDelete) return;

    setShifts((prev) => {
      const employeeShiftCount = countEmployeeScheduleWeekShifts(
        prev,
        shiftToDelete.scheduleId,
        shiftToDelete.locationId,
        shiftToDelete.employeeId,
        weekStart,
        weekEnd
      );
      setScheduleCounts((countsPrev) => {
        const current = countsPrev[shiftToDelete.scheduleId] ?? { totalShifts: 0, uniqueEmployees: 0, totalEmployeesAssigned: 0 };
        return {
          ...countsPrev,
          [shiftToDelete.scheduleId]: {
            ...current,
            totalShifts: Math.max(0, current.totalShifts - 1),
            uniqueEmployees: Math.max(0, current.uniqueEmployees - (employeeShiftCount === 1 ? 1 : 0)),
          },
        };
      });
      const next = prev.filter((s) => s.id !== id);
      shiftsRef.current = next;
      return next;
    });
    setShiftIdToDelete(null);
    setShowShiftForm(false);
    setEditingShift(null);
    setBreakMinsManuallySet(false);
    setShowPositionDropdown(false);
    markScheduleChangedInBackground();

    void (async () => {
      try {
        await shiftApi.delete(id);
        refreshScheduleCountsInBackground();
      } catch (error) {
        console.error('Error deleting shift:', error);
        setShifts((prev) => {
          const next = [...prev, shiftToDelete];
          shiftsRef.current = next;
          return next;
        });
        bumpScheduleCountsForAdd({
          employeeId: shiftToDelete.employeeId,
          locationId: shiftToDelete.locationId,
          scheduleId: shiftToDelete.scheduleId,
          startTime: shiftToDelete.startTime,
          endTime: shiftToDelete.endTime,
        });
        refreshScheduleCountsInBackground();
        const apiMessage =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
        alert(apiMessage || 'Failed to delete shift. Please try again.');
      }
    })();
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await reloadWeekView();
      await loadMarketplaceRequests();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Check if schedule is published and show warning; otherwise run the action
  const checkPublishedAndWarn = (action: () => void): boolean => {
    if (publicationStatus?.status === 'Published') {
      setPendingEditAction(() => action);
      setShowUnlockWarning(true);
      return false;
    }
    action();
    return true;
  };

  const handlePasteShift = useCallback((employeeId: number, date: Date, sourceShift: Shift) => {
    if (!selectedLocationId || !selectedScheduleId) return;

    const executePaste = () => {
      const startTimeStr = format(parseLocalDateTime(sourceShift.startTime), 'HH:mm');
      const endTimeStr = format(parseLocalDateTime(sourceShift.endTime), 'HH:mm');
      const { startTime: newStartTime, endTime: newEndTime } = buildShiftTimesForDate(date, startTimeStr, endTimeStr);

      if (checkTimeOverlap(employeeId, date, newStartTime, newEndTime)) {
        alert('Cannot paste shift: Time overlaps with an existing shift in this cell.');
        return;
      }

      const targetEmployee = employees.find((emp) => emp.id === employeeId);
      if (!targetEmployee?.isPlaceholder) {
        const unavailable = approvedUnavailabilityBlocksShift(
          getUnavailabilityFor(employeeId, date),
          parseLocalDateTime(newStartTime),
          parseLocalDateTime(newEndTime)
        );
        if (unavailable) {
          alert(unavailable);
          return;
        }
      }

      const copyColor = colorMode === 'position' && sourceShift.positionId
        ? getShiftColorForMode(sourceShift.positionId)
        : sourceShift.color;

      createShiftOptimistically({
        ...assigneeFromGridRow(employeeId),
        locationId: selectedLocationId,
        scheduleId: selectedScheduleId,
        startTime: newStartTime,
        endTime: newEndTime,
        color: copyColor,
        displayOption: sourceShift.displayOption,
        unpaidHours: sourceShift.breakUseGlobalSettings === false ? sourceShift.unpaidHours : 0,
        breakTimePaid: sourceShift.breakTimePaid ?? false,
        breakUseGlobalSettings: sourceShift.breakUseGlobalSettings !== false,
        positionId: sourceShift.positionId,
        notes: sourceShift.notes,
        isManualColorOverride: sourceShift.isManualColorOverride,
        isPayExtra: sourceShift.isPayExtra === true,
      });
    };

    checkPublishedAndWarn(executePaste);
  }, [selectedLocationId, selectedScheduleId, colorMode, createShiftOptimistically, publicationStatus?.status, getUnavailabilityFor, employees]);

  // Handle publish/unpublish/republish
  const handlePublish = async () => {
    if (isReadOnly) return;
    if (!selectedLocationId || !selectedScheduleId) return;
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await schedulePublicationStatusApi.publish(selectedLocationId, selectedScheduleId, weekStartStr);
      const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
      setPublicationStatus(updatedStatus);
      setShowActionsMenu(false);
    } catch (error) {
      console.error('Error publishing schedule:', error);
      alert('Failed to publish schedule. Please try again.');
    }
  };

  const handleUnpublish = async () => {
    if (isReadOnly) return;
    if (!selectedLocationId || !selectedScheduleId) return;
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await schedulePublicationStatusApi.unpublish(selectedLocationId, selectedScheduleId, weekStartStr);
      const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
      setPublicationStatus(updatedStatus);
      setShowActionsMenu(false);
    } catch (error) {
      console.error('Error unpublishing schedule:', error);
      alert('Failed to unpublish schedule. Please try again.');
    }
  };

  const handleRepublish = async () => {
    if (isReadOnly) return;
    if (!selectedLocationId || !selectedScheduleId) return;
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await schedulePublicationStatusApi.publish(selectedLocationId, selectedScheduleId, weekStartStr);
      const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
      setPublicationStatus(updatedStatus);
      setShowActionsMenu(false);
    } catch (error) {
      console.error('Error republishing schedule:', error);
      alert('Failed to republish schedule. Please try again.');
    }
  };

  const handleUnlockConfirm = () => {
    setShowUnlockWarning(false);
    if (pendingEditAction) {
      pendingEditAction();
      setPendingEditAction(null);
    }
  };

  const handleUnlockCancel = () => {
    setShowUnlockWarning(false);
    setPendingEditAction(null);
  };

  // Import Week handlers
  const handleWeekSelectChange = async (selectedWeekStart: Date) => {
    setImportWeekStart(selectedWeekStart);
    if (!selectedLocationId || !selectedScheduleId) return;

    try {
      const weekStartStr = format(selectedWeekStart, 'yyyy-MM-dd');
      const weekEndDate = endOfWeek(selectedWeekStart, { weekStartsOn });
      const weekEndStr = format(weekEndDate, 'yyyy-MM-dd');

      const shiftsData = await shiftApi.getAll({
        startDate: weekStartStr,
        endDate: weekEndStr,
        locationId: selectedLocationId,
        scheduleId: selectedScheduleId,
      });
      setImportShifts(shiftsData);
    } catch (error) {
      console.error('Error fetching shifts for import:', error);
      alert('Failed to fetch shifts for the selected week.');
    }
  };

  const handleImportConfirm = async () => {
    if (isReadOnly) return;
    if (!selectedLocationId || !selectedScheduleId || !importWeekStart) return;
    if (importWeekInProgressRef.current) return;
    importWeekInProgressRef.current = true;
    setImportWeekInProgress(true);

    try {
      const result = await schedulingApi.importWeek({
        locationId: selectedLocationId,
        scheduleId: selectedScheduleId,
        sourceWeekStartDate: format(importWeekStart, 'yyyy-MM-dd'),
        targetWeekStartDate: format(weekStart, 'yyyy-MM-dd'),
      });

      await reloadWeekView();

      if (publicationStatus?.status === 'Published' && selectedLocationId && selectedScheduleId) {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
        setPublicationStatus(updatedStatus);
      }

      setShowImportConfirm(false);
      setShowImportModal(false);
      setImportWeekStart(null);
      setImportShifts([]);

      const { importedCount, skippedHolidayCount } = result;
      alert(
        skippedHolidayCount > 0
          ? `Imported ${importedCount} record${importedCount === 1 ? '' : 's'}. Skipped ${skippedHolidayCount} on approved holiday or day off.`
          : `Successfully imported ${importedCount} record${importedCount === 1 ? '' : 's'}.`
      );
    } catch (error) {
      console.error('Error importing week:', error);
      alert('Failed to import week. Please try again.');
    } finally {
      importWeekInProgressRef.current = false;
      setImportWeekInProgress(false);
    }
  };

  // Calculate import summary
  const importSummary = useMemo(() => {
    if (importShifts.length === 0) return { totalHours: 0, recordCount: 0 };
    
    const totalHours = importShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
    
    return {
      totalHours,
      recordCount: importShifts.length,
    };
  }, [importShifts]);

  // Check if employee has any shifts for the current week
  const hasShiftsForWeek = (employeeId: number): boolean => {
    return shifts.some(shift => {
      if (shift.employeeId !== employeeId) return false;
      const shiftDate = parseLocalDateTime(shift.startTime);
      return shiftDate >= weekStart && shiftDate <= weekEnd;
    });
  };

  // Push empty rows to bottom while maintaining current sort order
  const handlePushEmptyRowsToBottom = async () => {
    if (isReadOnly) return;
    if (!selectedLocationId || !selectedScheduleId) return;

    try {
      // Get current employee order (with or without sort order)
      const currentOrder = employeeSortOrder.length > 0 
        ? [...employeeSortOrder]
        : filteredEmployees.map((emp, index) => ({
            employeeId: emp.id,
            sortOrder: index + 1
          }));

      // Separate employees into those with shifts and those without
      const employeesWithShifts: typeof currentOrder = [];
      const employeesWithoutShifts: typeof currentOrder = [];

      currentOrder.forEach(orderItem => {
        if (hasShiftsForWeek(orderItem.employeeId)) {
          employeesWithShifts.push(orderItem);
        } else {
          employeesWithoutShifts.push(orderItem);
        }
      });

      // Reorder: employees with shifts first (maintaining their order), then employees without shifts (maintaining their order)
      const newOrder = [
        ...employeesWithShifts.map((item, idx) => ({ ...item, sortOrder: idx + 1 })),
        ...employeesWithoutShifts.map((item, idx) => ({ ...item, sortOrder: employeesWithShifts.length + idx + 1 }))
      ];

      setEmployeeSortOrder(newOrder);

      // Save to database
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await employeeSortOrderApi.save(selectedLocationId, selectedScheduleId, weekStartStr, newOrder);
    } catch (error) {
      console.error('Error pushing empty rows to bottom:', error);
      alert('Failed to reorder employees. Please try again.');
    }
  };

  const navigateWeek = (direction: 'prev' | 'next' | 'today') => {
    if (direction === 'prev') {
      setCurrentWeek(addDays(currentWeek, -7));
    } else if (direction === 'next') {
      setCurrentWeek(addDays(currentWeek, 7));
    } else {
      setCurrentWeek(new Date());
    }
  };

  const navigateDay = (direction: 'prev' | 'next' | 'today') => {
    if (direction === 'prev') {
      setCurrentWeek(addDays(currentWeek, -1));
    } else if (direction === 'next') {
      setCurrentWeek(addDays(currentWeek, 1));
    } else {
      setCurrentWeek(new Date());
    }
  };

  const navigateMonth = (direction: 'prev' | 'next' | 'today') => {
    if (direction === 'prev') {
      setCurrentWeek(addMonths(startOfMonth(currentWeek), -1));
    } else if (direction === 'next') {
      setCurrentWeek(addMonths(startOfMonth(currentWeek), 1));
    } else {
      setCurrentWeek(new Date());
    }
  };

  const viewModeToggle = (
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden flex-shrink-0" role="group" aria-label="Schedule view">
      {([
        { id: 'week' as const, label: 'Weekly' },
        { id: 'day' as const, label: 'Daily' },
        { id: 'month' as const, label: 'Monthly' },
      ]).map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => setViewMode(option.id)}
          className={`px-3 py-2 text-sm font-medium transition-colors ${
            viewMode === option.id
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-4 h-full flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between flex-shrink-0 relative gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {viewModeToggle}
          {viewMode === 'week' && (
            <WeekNavigator
              weekStart={weekStart}
              weekEnd={weekEnd}
              weekStartsOn={weekStartsOn}
              currentWeek={currentWeek}
              onPreviousWeek={() => navigateWeek('prev')}
              onNextWeek={() => navigateWeek('next')}
              onCurrentWeek={() => navigateWeek('today')}
              onWeekSelect={(ymd) => setCurrentWeek(parseLocalDate(ymd))}
              currentWeekLabel="This Week"
            />
          )}
          {viewMode === 'day' && (
            <DayNavigator
              day={currentWeek}
              weekStartsOn={weekStartsOn}
              onPreviousDay={() => navigateDay('prev')}
              onNextDay={() => navigateDay('next')}
              onToday={() => navigateDay('today')}
              onDaySelect={(ymd) => setCurrentWeek(parseLocalDate(ymd))}
            />
          )}
          {viewMode === 'month' && (
            <div className="flex items-center flex-wrap gap-3">
              <MonthNavigator
                month={currentWeek}
                onPreviousMonth={() => navigateMonth('prev')}
                onNextMonth={() => navigateMonth('next')}
                onMonthSelect={(month) => setCurrentWeek(startOfMonth(month))}
              />
              <button
                type="button"
                onClick={() => navigateMonth('today')}
                disabled={isSameMonth(currentWeek, new Date())}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                This Month
              </button>
            </div>
          )}
        </div>

        {/* Publication Status Badge - Centered */}
        <div className="absolute left-1/2 transform -translate-x-1/2 pointer-events-none hidden xl:block">
          {publicationStatus?.status === 'Published' && (
            <div className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-semibold">
              <CheckCircle className="w-4 h-4" />
              <span>Schedule Published</span>
            </div>
          )}
          {publicationStatus?.status === 'Changed' && (
            <div className="flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1.5 rounded-full text-sm font-semibold">
              <AlertCircle className="w-4 h-4" />
              <span>Schedule Changed</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-3 py-2 border rounded hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh schedules and shifts"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="sr-only">Refresh</span>
          </button>
          <select
            value={selectedScheduleId || ''}
            onChange={(e) => {
              setSelectedScheduleId(Number(e.target.value));
            }}
            className="px-3 py-2 border rounded"
          >
            <option value="">Select Schedule</option>
            {dropdownSchedules.map(sched => {
              const counts = scheduleCounts[sched.id];
              const employeesWithShifts = counts?.uniqueEmployees || 0;
              const shiftCount = counts?.totalShifts || 0;
              const totalEmployeesAssigned = counts?.totalEmployeesAssigned || 0;
              const schedStatus = allSchedulesPublicationStatus[sched.id]?.status;
              const statusPrefix = schedStatus === 'Published' ? '✅ ' : schedStatus === 'Changed' ? '⚠️ ' : '○ ';
              const inactiveSuffix = !isScheduleActive(sched) ? ' (Inactive)' : '';
              const displayText = counts && (totalEmployeesAssigned > 0 || employeesWithShifts > 0 || shiftCount > 0)
                ? `${statusPrefix}${sched.name}${inactiveSuffix} 👥${totalEmployeesAssigned} 👤${employeesWithShifts} 🕐${shiftCount}`
                : `${statusPrefix}${sched.name}${inactiveSuffix}`;
              return (
                <option
                  key={sched.id}
                  value={sched.id}
                  style={!isScheduleActive(sched) ? { color: '#dc2626' } : undefined}
                  title={`${schedStatus || 'Unpublished'} · ${totalEmployeesAssigned} employees assigned, ${employeesWithShifts} with shifts this week, ${shiftCount} total shifts`}
                >
                  {displayText}
                </option>
              );
            })}
          </select>
          
          {/* Actions Button */}
          {selectedScheduleId && !isReadOnly && (
            <div className="relative">
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                className="px-3 py-2 border rounded hover:bg-gray-50 flex items-center gap-1"
              >
                <span>Actions</span>
                <MoreVertical className="w-4 h-4" />
              </button>
              
              {showActionsMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowActionsMenu(false)}
                  />
                  <div className="absolute right-0 mt-1 w-56 bg-white border rounded shadow-lg z-20">
                    <button
                      onClick={() => {
                        setShowImportModal(true);
                        setShowActionsMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-b"
                    >
                      <Download className="w-4 h-4" />
                      <span>Import Week</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowActionsMenu(false);
                        checkPublishedAndWarn(() => setShowScheduleFileImportModal(true));
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-b"
                    >
                      <FileUp className="w-4 h-4" />
                      <span>Import from File</span>
                    </button>
                    <Link
                      to={`/scheduling/import-history?scheduleId=${selectedScheduleId}`}
                      onClick={() => setShowActionsMenu(false)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-b text-gray-800"
                    >
                      <History className="w-4 h-4" />
                      <span>View Import History</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setShowActionsMenu(false);
                        checkPublishedAndWarn(() => setShowBulkDeleteScheduleShiftsModal(true));
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-b text-red-700"
                    >
                      <Trash className="w-4 h-4" />
                      <span>Bulk delete shifts</span>
                    </button>
                    {publicationStatus?.status === 'Published' ? (
                      <button
                        onClick={handleUnpublish}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Unlock className="w-4 h-4" />
                        <span>Unpublish Schedule</span>
                      </button>
                    ) : publicationStatus?.status === 'Changed' ? (
                      <button
                        onClick={handleRepublish}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Lock className="w-4 h-4" />
                        <span>Re-publish Schedule</span>
                      </button>
                    ) : (
                      <button
                        onClick={handlePublish}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Lock className="w-4 h-4" />
                        <span>Publish Schedule</span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {isSelectedLocationInactive && selectedLocation && (
        <InactiveLocationBanner locationName={selectedLocation.name} />
      )}

      {isScheduleReadOnly && selectedSchedule && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 flex-shrink-0">
          <Lock className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>{selectedSchedule.name} (Inactive)</strong> is read-only. You can view existing shifts for this week but cannot create or modify them.
          </span>
        </div>
      )}

      {selectedSchedule && debouncedSelectedLocationId && (
        <div
          className={`mb-4 flex items-start gap-2 rounded-md border px-4 py-2 text-sm flex-shrink-0 ${
            expectedCountWarning
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-slate-200 bg-slate-50 text-slate-800'
          }`}
        >
          <Calculator className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-medium">Expected counts for {selectedSchedule.name}</div>
              <span className="inline-flex items-center rounded border border-slate-200 bg-white px-2 py-0.5 text-xs">
                {weekExpectedCounts?.mode === 'Override' ? 'Override' : 'From Schedule Settings'}
              </span>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={() => setShowExpectedCountsModal(true)}
                  className="text-xs font-medium text-blue-700 hover:text-blue-800 underline"
                >
                  Edit this week
                </button>
              )}
            </div>
            <div className="mt-1 flex flex-wrap gap-2">
              {expectedCountProgress?.weekShifts.expected ? (
                <span
                  className={`inline-flex items-center rounded border px-2 py-0.5 text-xs ${
                    expectedCountProgress.weekShifts.actual >= expectedCountProgress.weekShifts.expected
                      ? 'border-amber-300 bg-white'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  Shifts this week {expectedCountProgress.weekShifts.actual}/{expectedCountProgress.weekShifts.expected}
                </span>
              ) : (
                <span className="text-xs text-slate-600">
                  Defaults apply every week. Open the popup to use settings or override this week.
                </span>
              )}
              {expectedCountProgress?.dayShifts
                .filter((day) => day.expected > 0)
                .map((day) => (
                  <span
                    key={day.date}
                    className={`inline-flex items-center rounded border px-2 py-0.5 text-xs ${
                      day.actual >= day.expected ? 'border-amber-300 bg-white' : 'border-slate-200 bg-white'
                    }`}
                  >
                    {day.isBankHoliday ? 'BH' : format(parseLocalDate(day.date), 'EEE')} {day.actual}/{day.expected}
                  </span>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Show blank screen if "All Locations" is selected */}
      {!selectedLocationId ? (
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-50">
          <MapPin className="w-24 h-24 text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Select a Location</h2>
          <p className="text-gray-600 text-center max-w-md">
            Please select a specific location from the dropdown in the top navigation bar to access scheduling features.
          </p>
        </div>
      ) : (
        <div className="flex-1 flex gap-4 min-h-0" style={{ height: 'calc(100vh - 250px)' }}>
          {viewMode === 'month' ? (
            <SchedulingMonthView
              month={currentWeek}
              weekStartsOn={weekStartsOn}
              shifts={shifts.filter(
                (s) =>
                  Number(s.scheduleId) === Number(selectedScheduleId) &&
                  Number(s.locationId) === Number(debouncedSelectedLocationId)
              )}
              employees={employees}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              colorMode={colorMode}
              onColorModeChange={setColorMode}
              positions={positions}
              approvedHolidays={weekView?.approvedHolidays}
              unavailability={weekView?.unavailability}
              loading={loadingWeekView}
              marketplaceByShiftId={marketplaceByShiftId}
            />
          ) : (
            <>
          {/* Shift Timings Sidebar */}
        <div className={`w-64 border rounded-lg flex flex-col min-h-0 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`} style={{ maxHeight: '100%' }}>
          <div className="flex-shrink-0 p-4 pb-3 border-b bg-white rounded-t-lg">
            <h3 className="font-semibold mb-3">Shift Timings</h3>

            {/* Color Mode Radio Buttons */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-2">Color By</div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="colorMode"
                    value="shift"
                    checked={colorMode === 'shift'}
                    onChange={(e) => setColorMode(e.target.value as 'shift' | 'position')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Shift Color</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="colorMode"
                    value="position"
                    checked={colorMode === 'position'}
                    onChange={(e) => setColorMode(e.target.value as 'shift' | 'position')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Position Color</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 pt-3 min-h-0">
          <div className="space-y-2">
            {sortedShiftTemplates.map(template => (
              <div
                key={template.id}
                draggable={!isReadOnly}
                onDragStart={(e) => handleDragStart(e, template)}
                onDragEnd={() => setDraggedTemplate(null)}
                className={`p-3 border rounded flex items-center justify-between ${isReadOnly ? 'cursor-not-allowed' : 'cursor-move hover:bg-gray-50'}`}
                style={{ backgroundColor: shiftTemplateLightBackground(template.color), borderColor: template.color }}
              >
                <div className="font-medium text-sm">
                  {template.startTime} - {template.endTime}
                </div>
                <div className="font-medium text-sm">
                  {template.hours}h
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="flex-1 border rounded-lg flex flex-col relative" style={{ maxHeight: '100%' }}>
          {/* Loading Overlay */}
          {loadingWeekView && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <div className="text-sm text-gray-600 font-medium">Loading shifts...</div>
              </div>
            </div>
          )}
          
          {/* Header Row */}
          <div className="grid border-b sticky top-0 bg-white z-10 flex-shrink-0" style={{ gridTemplateColumns }}>
            <div className="p-3 font-semibold border-r border-gray-300">Employee</div>
            {weekDays.map(day => {
              const dayKey = format(day, 'yyyy-MM-dd');
              const dayExpected = expectedCountProgress?.dayShifts.find((d) => d.date === dayKey);
              const overDay =
                !!dayExpected &&
                dayExpected.expected > 0 &&
                dayExpected.actual >= dayExpected.expected;
              return (
              <div key={day.toISOString()} className="p-3 text-center border-r border-gray-300">
                <div className="font-semibold">{format(day, 'EEE')}</div>
                <div className="text-xs text-gray-500">{formatCompanyDate(day)}</div>
                {dayExpected && dayExpected.expected > 0 && (
                  <div className={`text-[11px] mt-0.5 ${overDay ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                    {dayExpected.actual}/{dayExpected.expected} shifts{dayExpected.isBankHoliday ? ' · BH' : ''}
                  </div>
                )}
              </div>
              );
            })}
            <div className="p-3 font-semibold text-center border-l border-gray-100 bg-gray-50 relative" ref={totalHeaderDropdownRef}>
              <div className="flex items-center justify-center gap-1">
                <div className="flex flex-col items-center">
                  <div>Total</div>
                  <div className="text-xs font-normal">Week</div>
                </div>
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTotalDropdown(!showTotalDropdown);
                    }}
                    className="p-0.5 hover:bg-gray-200 rounded flex items-center justify-center transition-colors flex-shrink-0"
                    title="Toggle display mode"
                  >
                    <ChevronRight className={`w-3 h-3 text-gray-700 transition-transform ${showTotalDropdown ? 'rotate-90' : ''}`} />
                  </button>
                  {showTotalDropdown && (
                    <div className="absolute left-full -top-5 ml-0.5 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[60px]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTotalDisplayMode('hours');
                          setShowTotalDropdown(false);
                        }}
                        className={`w-full text-left px-2 py-1.5 text-xs hover:bg-gray-100 whitespace-nowrap ${totalDisplayMode === 'hours' ? 'bg-blue-50 font-semibold' : ''}`}
                      >
                        Hours
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTotalDisplayMode('cost');
                          setShowTotalDropdown(false);
                        }}
                        className={`w-full text-left px-2 py-1.5 text-xs hover:bg-gray-100 whitespace-nowrap ${totalDisplayMode === 'cost' ? 'bg-blue-50 font-semibold' : ''}`}
                      >
                        Cost
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Search and Sort Bar */}
          <div className="border-b bg-gray-50 p-2 flex items-center justify-between gap-2 sticky top-[60px] z-10 flex-shrink-0">
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search employees and placeholders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 pl-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
              </div>
            </div>
            <button
              onClick={() => void openPlaceholderModal()}
              disabled={isReadOnly || !selectedScheduleId}
              className="px-4 py-2 border rounded-md hover:bg-gray-100 flex items-center gap-2 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Manage placeholder seats for this schedule"
            >
              <Users className="w-4 h-4" />
              <span>Placeholders</span>
            </button>
            <button
              onClick={handlePushEmptyRowsToBottom}
              disabled={isReadOnly}
              className="px-4 py-2 border rounded-md hover:bg-gray-100 flex items-center gap-2 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Push empty rows to bottom"
            >
              <ArrowDownToLine className="w-4 h-4" />
              <span>Push Empty</span>
            </button>
            <button
              onClick={() => {
                // Initialize sort order: use existing order if available, otherwise use current employee order
                if (employeeSortOrder.length === 0) {
                  const currentOrder = filteredEmployees.map((emp, index) => ({
                    employeeId: emp.id,
                    sortOrder: index + 1
                  }));
                  setEmployeeSortOrder(currentOrder);
                }
                setShowSortModal(true);
              }}
              disabled={isReadOnly}
              className="px-4 py-2 border rounded-md hover:bg-gray-100 flex items-center gap-2 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Sort</span>
              <span>⇅</span>
            </button>
          </div>

          {/* Employee Rows - Scrollable area */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {filteredEmployees.map(employee => {
              const isDragged = draggedEmployeeId === employee.id;
              const isDragOver = dragOverEmployeeId === employee.id;
              
              return (
                <div 
                  key={employee.id} 
                  className={`grid border-b ${isDragged ? 'opacity-50' : ''} ${isDragOver ? 'bg-blue-50' : ''}`}
                  style={{ gridTemplateColumns }}
                  draggable={!isReadOnly && !employee.isPlaceholder}
                  onDragStart={(e) => {
                    if (isReadOnly) return;
                    setDraggedEmployeeId(employee.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={() => {
                    setDraggedEmployeeId(null);
                    setDragOverEmployeeId(null);
                  }}
                  onDragOver={(e) => {
                    // Only be a drop target when dragging employee rows (reorder), not shift templates
                    if (!draggedEmployeeId) {
                      return; // shift template drag - let cell handle it
                    }
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverEmployeeId !== employee.id) {
                      setDragOverEmployeeId(employee.id);
                    }
                  }}
                  onDragLeave={() => {
                    setDragOverEmployeeId(null);
                  }}
                  onDrop={async (e) => {
                    if (isReadOnly) return;
                    e.preventDefault();
                    e.stopPropagation(); // Prevent shift drop handler from firing
                    if (draggedEmployeeId && draggedEmployeeId !== employee.id) {
                      // Update sort order
                      const newOrder = [...employeeSortOrder];
                      const draggedIndex = newOrder.findIndex(o => o.employeeId === draggedEmployeeId);
                      const targetIndex = newOrder.findIndex(o => o.employeeId === employee.id);
                      
                      if (draggedIndex !== -1 && targetIndex !== -1) {
                        // Both employees are in sort order - reorder
                        const [removed] = newOrder.splice(draggedIndex, 1);
                        newOrder.splice(targetIndex, 0, removed);
                        // Reassign sort orders
                        newOrder.forEach((item, idx) => {
                          item.sortOrder = idx + 1;
                        });
                      } else if (draggedIndex === -1 && targetIndex !== -1) {
                        // Dragged employee not in sort order, add it at target position
                        newOrder.splice(targetIndex, 0, { employeeId: draggedEmployeeId, sortOrder: targetIndex + 1 });
                        newOrder.forEach((item, idx) => {
                          item.sortOrder = idx + 1;
                        });
                      } else if (draggedIndex !== -1 && targetIndex === -1) {
                        // Target employee not in sort order, add it after dragged position
                        const insertIndex = draggedIndex + 1;
                        newOrder.splice(insertIndex, 0, { employeeId: employee.id, sortOrder: insertIndex + 1 });
                        newOrder.forEach((item, idx) => {
                          item.sortOrder = idx + 1;
                        });
                      } else {
                        // Neither in sort order - initialize with both
                        const draggedEmp = filteredEmployees.find(e => e.id === draggedEmployeeId);
                        const targetEmp = filteredEmployees.find(e => e.id === employee.id);
                        if (draggedEmp && targetEmp) {
                          const draggedPos = filteredEmployees.findIndex(e => e.id === draggedEmployeeId);
                          const targetPos = filteredEmployees.findIndex(e => e.id === employee.id);
                          // Add both employees maintaining their relative order
                          if (draggedPos < targetPos) {
                            newOrder.push({ employeeId: draggedEmployeeId, sortOrder: newOrder.length + 1 });
                            newOrder.push({ employeeId: employee.id, sortOrder: newOrder.length + 1 });
                          } else {
                            newOrder.push({ employeeId: employee.id, sortOrder: newOrder.length + 1 });
                            newOrder.push({ employeeId: draggedEmployeeId, sortOrder: newOrder.length + 1 });
                          }
                          newOrder.forEach((item, idx) => {
                            item.sortOrder = idx + 1;
                          });
                        }
                      }
                      
                      setEmployeeSortOrder(newOrder);
                      
                      // Save to database immediately
                      if (selectedLocationId && selectedScheduleId) {
                        try {
                          const weekStartStr = format(weekStart, 'yyyy-MM-dd');
                          await employeeSortOrderApi.save(selectedLocationId, selectedScheduleId, weekStartStr, newOrder);
                        } catch (error) {
                          console.error('Error saving sort order:', error);
                          // Revert on error
                          setEmployeeSortOrder([...employeeSortOrder]);
                        }
                      }
                    }
                    setDraggedEmployeeId(null);
                    setDragOverEmployeeId(null);
                  }}
                >
                  <div className={`p-3 border-r border-gray-300 flex items-center gap-2 ${isReadOnly || employee.isPlaceholder ? 'cursor-default' : 'cursor-move'}`}>
                    {!employee.isPlaceholder && <GripVertical className="w-4 h-4 text-gray-400" />}
                    <div className="min-w-0">
                      <div className="font-medium text-sm">
                        {employee.firstName} {employee.lastName}
                        {employee.isPlaceholder && (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                            Seat
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {employee.isPlaceholder ? 'Placeholder seat' : employee.email}
                      </div>
                      {employee.isPlaceholder && !isReadOnly && (
                        <button
                          type="button"
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
                          onClick={() => {
                            setAssignSeat({
                              id: employee.placeholderId || placeholderIdFromRowId(employee.id),
                              name: `${employee.firstName} ${employee.lastName}`.trim(),
                            });
                            setAssignEmployeeId(0);
                            setAssignQuery('');
                          }}
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          Assign person
                        </button>
                      )}
                      {(() => {
                        const weekItems = employeeWeekUnavailability(employee.id);
                        if (weekItems.length === 0) return null;
                        const pending = weekItems.filter((u) => u.status === 'Pending');
                        const approved = weekItems.filter((u) => u.status === 'Approved');
                        const chip = (items: SchedulingUnavailability[], pendingChip: boolean) => {
                          const labels = Array.from(new Set(items.map((u) => {
                            const [y, m, d] = u.date.split('-').map(Number);
                            const day = format(new Date(y, (m || 1) - 1, d || 1), 'EEE');
                            if (u.isAllDay) return day;
                            return `${day} ${(u.startTime || '').slice(0, 5)}`;
                          })));
                          const title = items.map(unavailabilityTooltip).join('\n');
                          return (
                            <span
                              key={pendingChip ? 'p' : 'a'}
                              title={title}
                              className={`inline-block mt-1 mr-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                                pendingChip
                                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                                  : 'bg-slate-100 text-slate-800 border-slate-200'
                              }`}
                            >
                              {pendingChip ? 'Pending ' : 'Unavailable '}
                              {labels.join(', ')}
                            </span>
                          );
                        };
                        return (
                          <div className="mt-0.5 leading-tight">
                            {approved.length > 0 && chip(approved, false)}
                            {pending.length > 0 && chip(pending, true)}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                {weekDays.map(day => {
                  const dayShifts = getShiftsForEmployeeAndDate(employee.id, day);
                  const cellKey = `${employee.id}-${day.toISOString()}`;
                  const isDropTarget = dragOverCell === cellKey;
                  const isHovered = hoveredCell === cellKey;
                  const isEmpty = dayShifts.length === 0;
                  const approvedHoliday = getApprovedHoliday(employee.id, day);
                  const hasApprovedHoliday = !!approvedHoliday;
                  const dayUnavailability = getUnavailabilityFor(employee.id, day);
                  const hasApprovedAllDay = dayUnavailability.some((u) => u.status === 'Approved' && u.isAllDay);
                  const cellLocked = hasApprovedHoliday || hasApprovedAllDay;
                  // Check if there are any shifts from the current schedule
                  const hasCurrentScheduleShift = dayShifts.some(isCurrentScheduleShift);
                  // Show + icon if empty OR if only cross-schedule/cross-location shifts exist
                  const canAddShift = !isReadOnly && !cellLocked && (isEmpty || !hasCurrentScheduleShift);

                  return (
                    <div
                      key={day.toISOString()}
                      data-cell-key={cellKey}
                      className={`p-2 min-h-20 border-r border-gray-300 last:border-r-0 relative group ${
                        hasApprovedHoliday
                          ? 'bg-green-50'
                          : hasApprovedAllDay
                          ? 'bg-slate-50'
                          : isDropTarget
                          ? 'bg-blue-50'
                          : 'bg-white'
                      }`}
                      onMouseEnter={() => setHoveredCell(cellKey)}
                      onMouseLeave={() => {
                        setHoveredCell(null);
                        setHoveredShiftId(null);
                      }}
                      onDragEnter={(e) => {
                        if (!draggedEmployeeId && !cellLocked) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }
                      }}
                      onDragOver={(e) => {
                        // Only handle shift template drag, not employee row drag
                        if (!draggedEmployeeId && !cellLocked) {
                          handleDragOver(e, employee.id, day);
                        }
                      }}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => {
                        if (!draggedEmployeeId && !isReadOnly && !cellLocked) {
                          handleDrop(e, employee.id, day);
                        }
                      }}
                    >
                      {hasApprovedHoliday && (
                        <div
                          className="mb-1 px-1.5 py-1 rounded text-xs font-semibold bg-green-100 text-green-800 border border-green-200 flex items-center gap-1"
                          title={approvedHoliday.leaveTypeName || approvedHoliday.holidayType || 'Approved holiday'}
                        >
                          <Calendar className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">
                            {approvedHoliday.holidayType || approvedHoliday.leaveTypeName || 'Holiday'}
                          </span>
                          <Lock className="w-3 h-3 flex-shrink-0 ml-auto opacity-70" />
                        </div>
                      )}
                      {dayUnavailability.map((item) => (
                        <div
                          key={`${item.requestId}-${item.status}-${item.startTime || 'all'}`}
                          className={`mb-1 px-1.5 py-1 rounded text-xs font-semibold border flex items-center gap-1 ${
                            item.status === 'Pending'
                              ? 'bg-amber-50 text-amber-800 border-amber-200'
                              : 'bg-slate-100 text-slate-800 border-slate-200'
                          }`}
                          title={unavailabilityTooltip(item)}
                        >
                          <span className="truncate">{formatUnavailabilityBanner(item)}</span>
                          {item.status === 'Approved' && item.isAllDay && (
                            <Lock className="w-3 h-3 flex-shrink-0 ml-auto opacity-70" />
                          )}
                        </div>
                      ))}

                      {/* Show + icon on empty cells or cells with only cross-schedule/cross-location shifts when hovering */}
                      {canAddShift && isHovered && (
                        <button
                          onClick={() => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            setEditingShift({
                              id: 0,
                              employeeId: employee.id,
                              locationId: selectedLocationId!,
                              scheduleId: selectedScheduleId!,
                              startTime: `${dateStr}T00:00:00`,
                              endTime: `${dateStr}T00:00:00`,
                              color: '#3b82f6',
                              displayOption: 'time',
                            } as Shift);
                            setBreakMinsManuallySet(false);
                            const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
                            setFormData({
                              name: '',
                              startTime: '',
                              endTime: '',
                              hours: 0,
                              unpaidBreakMins: 0,
                              breakTimePaid: defaultBreakTimePaid,
                              breakUseGlobalSettings: true,
                              totalHours: 0,
                              color: '#3b82f6',
                              displayOption: 'time',
                              positionId: employee.profile?.mainPositionId,
                              shiftGroupId: undefined,
                              notes: '',
                              isManualColorOverride: false,
                              originalColor: '#3b82f6',
                              isPayExtra: false,
                            });
                            setShowShiftForm(true);
                          }}
                          className="absolute top-1 right-1 p-1 hover:bg-blue-100 rounded bg-white shadow-sm z-10"
                          title="Add shift"
                        >
                          <Plus className="w-4 h-4 text-blue-600" />
                        </button>
                      )}

                      {/* Show paste icon if shift is copied and cell is hovered */}
                      {copiedShift && isHovered && !isReadOnly && !cellLocked && (
                        <button
                          onClick={() => handlePasteShift(employee.id, day, copiedShift)}
                          className="absolute bottom-1 right-1 p-1 hover:bg-green-100 rounded bg-white shadow-sm z-10"
                          title="Paste shift"
                        >
                          <Clipboard className="w-4 h-4 text-green-600" />
                        </button>
                      )}

                      {dayShifts.map(shift => {
                        const isShiftHovered = hoveredShiftId === shift.id;
                        const isCurrentSchedule = isCurrentScheduleShift(shift);
                        const isCrossSchedule = isSameLocationShift(shift) && !isCurrentScheduleShift(shift);
                        const isCrossLocation = !isSameLocationShift(shift);
                        const isShiftOnApprovedHoliday = hasApprovedHoliday && isCurrentSchedule;
                        const hasUnavailabilityConflict = shiftConflictsWithUnavailability(dayUnavailability, shift);
                        const marketplacePending = marketplaceByShiftId.get(shift.id);
                        const marketplaceLockTitle = marketplacePending
                          ? marketplaceEditLockTitle(marketplacePending)
                          : undefined;
                        const canEditShift =
                          isCurrentSchedule && !isReadOnly && !isShiftOnApprovedHoliday && !marketplacePending;
                        
                        // Determine styling based on shift type
                        const shiftStyle = isCurrentSchedule 
                          ? { backgroundColor: getShiftDisplayColor(shift), color: 'white' }
                          : { backgroundColor: 'transparent', color: isCrossLocation ? '#9ca3af' : '#374151' };
                        
                        const shiftClassName = isCurrentSchedule
                          ? `p-2 mb-1 rounded text-xs relative${isShiftOnApprovedHoliday ? ' opacity-90' : ''}${hasUnavailabilityConflict ? ' ring-1 ring-amber-400' : ''}`
                          : isCrossLocation
                          ? "p-2 mb-1 rounded text-xs relative opacity-60"
                          : "p-2 mb-1 rounded text-xs relative border border-gray-300";
                        
                        return (
                          <div
                            key={shift.id}
                            id={`schedule-shift-${shift.id}`}
                            className={shiftClassName}
                            style={shiftStyle}
                            onMouseEnter={() => setHoveredShiftId(shift.id)}
                            onMouseLeave={() => setHoveredShiftId(null)}
                          >
                            {isShiftHovered && isCurrentSchedule && !isReadOnly ? (
                              isShiftOnApprovedHoliday ? (
                                <div className="flex items-center justify-center bg-white bg-opacity-20 rounded p-1.5">
                                  <button
                                    disabled={!!marketplacePending}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (marketplacePending) return;
                                      checkPublishedAndWarn(() => setShiftIdToDelete(shift.id));
                                    }}
                                    className={`p-1.5 rounded transition-colors ${
                                      marketplacePending
                                        ? 'opacity-40 cursor-not-allowed'
                                        : 'hover:bg-white hover:bg-opacity-30'
                                    }`}
                                    title={marketplaceLockTitle ?? 'Delete shift'}
                                  >
                                    <Trash2 className="w-4 h-4 text-white stroke-[2.5]" />
                                  </button>
                                </div>
                              ) : (
                              <div className="flex items-center justify-center gap-3 bg-white bg-opacity-20 rounded p-1.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCopiedShift(shift);
                                  }}
                                  className="p-1.5 hover:bg-white hover:bg-opacity-30 rounded transition-colors"
                                  title="Copy shift"
                                >
                                  <Copy className="w-4 h-4 text-white stroke-[2.5]" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const dateStr = format(day, 'yyyy-MM-dd');
                                    setEditingShift({
                                      id: 0,
                                      employeeId: employee.id,
                                      locationId: selectedLocationId!,
                                      scheduleId: selectedScheduleId!,
                                      startTime: `${dateStr}T00:00:00`,
                                      endTime: `${dateStr}T00:00:00`,
                                      color: '#3b82f6',
                                      displayOption: 'time',
                                    } as Shift);
                                    setBreakMinsManuallySet(false);
                                    const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
                                    setFormData({
                                      name: '',
                                      startTime: '',
                                      endTime: '',
                                      hours: 0,
                                      unpaidBreakMins: 0,
                                      breakTimePaid: defaultBreakTimePaid,
                                      breakUseGlobalSettings: true,
                                      totalHours: 0,
                                      color: '#3b82f6',
                                      displayOption: 'time',
                                      positionId: employee.profile?.mainPositionId,
                                      shiftGroupId: undefined,
                                      notes: '',
                                      isManualColorOverride: false,
                                      originalColor: '#3b82f6',
                                      isPayExtra: false,
                                    });
                                    setShowShiftForm(true);
                                  }}
                                  className="p-1.5 hover:bg-white hover:bg-opacity-30 rounded transition-colors"
                                  title="Add shift"
                                >
                                  <Plus className="w-4 h-4 text-white stroke-[2.5]" />
                                </button>
                                <button
                                  disabled={!!marketplacePending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (marketplacePending) return;
                                    handleEditShift(shift);
                                  }}
                                  className={`p-1.5 rounded transition-colors ${
                                    marketplacePending
                                      ? 'opacity-40 cursor-not-allowed'
                                      : 'hover:bg-white hover:bg-opacity-30'
                                  }`}
                                  title={marketplaceLockTitle ?? 'Edit shift'}
                                >
                                  <Edit className="w-4 h-4 text-white stroke-[2.5]" />
                                </button>
                              </div>
                              )
                            ) : (
                              <>
                                <div 
                                  className={`font-semibold ${canEditShift ? 'cursor-pointer' : 'cursor-default'}`}
                                  title={marketplaceLockTitle}
                                  onClick={() => {
                                    if (canEditShift) {
                                      handleEditShift(shift);
                                    }
                                  }}
                                >
                                  {format(parseLocalDateTime(shift.startTime), 'HH:mm')} - {format(parseLocalDateTime(shift.endTime), 'HH:mm')}
                                </div>
                                {shift.position && (
                                  <div className={`text-xs ${isCurrentSchedule ? 'opacity-90' : 'opacity-70'}`}>
                                    {shift.position.title}
                                  </div>
                                )}
                                {(isCrossSchedule || isCrossLocation) && (
                                  <div className="text-xs opacity-70 italic">
                                    <a
                                      href={buildSchedulingShiftUrl(shift, currentWeek)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {getShiftScheduleName(shift, schedules)}
                                    </a>
                                  </div>
                                )}
                                {isCrossLocation && (() => {
                                  const location = locations.find(l => l.id === shift.locationId);
                                  return location ? (
                                    <div className="text-xs opacity-70 italic">
                                      {location.name}
                                    </div>
                                  ) : null;
                                })()}
                              </>
                            )}
                            {marketplacePending && (
                              <MarketplaceCoverBadge
                                request={marketplacePending}
                                variant={isCurrentSchedule ? 'onColor' : 'onLight'}
                              />
                            )}
                            {!!shift.restRuleWarnings?.length && (
                              <button
                                type="button"
                                className={`absolute top-1 right-1 p-0.5 rounded-full shadow-sm ${
                                  isCurrentSchedule
                                    ? 'bg-white/90 text-amber-600 hover:bg-white'
                                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                }`}
                                title="Working time / rest reminder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRestRuleWarningModal({
                                    warnings: shift.restRuleWarnings!,
                                    shiftId: shift.id,
                                  });
                                }}
                              >
                                <AlertCircle className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {/* Total column for employee */}
                <div className="p-3 border-l border-gray-100 bg-gray-50 text-center">
                  <div className="font-semibold">
                    {totalDisplayMode === 'cost' 
                      ? (
                        <button
                          onClick={() => {
                            setSelectedEmployeeForBreakdown(employee);
                            setShowCostBreakdownModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        >
                          €{(totalsCache.employeeCostCurrent[employee.id] ?? 0).toFixed(2)}
                        </button>
                      )
                      : `${(totalsCache.employeeHoursCurrent[employee.id] ?? 0).toFixed(2)}h`
                    }
                  </div>
                  {/* All schedules total (same location) - smaller font */}
                  {(() => {
                    const allSchedulesTotal = totalDisplayMode === 'cost'
                      ? (totalsCache.employeeCostAll[employee.id] ?? 0)
                      : (totalsCache.employeeHoursAll[employee.id] ?? 0);
                    const currentScheduleTotal = totalDisplayMode === 'cost'
                      ? (totalsCache.employeeCostCurrent[employee.id] ?? 0)
                      : (totalsCache.employeeHoursCurrent[employee.id] ?? 0);
                    
                    // Only show if all schedules total is different from current schedule total
                    if (Math.abs(allSchedulesTotal - currentScheduleTotal) > 0.01) {
                      return (
                        <div className="text-xs text-gray-500 mt-1">
                          {totalDisplayMode === 'cost'
                            ? `All: €${allSchedulesTotal.toFixed(2)}`
                            : `All: ${allSchedulesTotal.toFixed(2)}h`
                          }
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            );
            })}
          </div>
          
          {/* Total Row - Fixed at bottom */}
          <div className="grid border-t-2 border-gray-400 bg-gray-100 flex-shrink-0" style={{ gridTemplateColumns }}>
            <div className="p-3 font-semibold border-r border-gray-300 bg-gray-100">
              Total
            </div>
            {weekDays.map((day, index) => (
              <div 
                key={day.toISOString()} 
                className={`p-2 text-center ${index < weekDays.length - 1 ? 'border-r border-gray-300' : ''}`}
              >
                <div className="font-semibold">
                  {totalDisplayMode === 'cost'
                    ? `€${(totalsCache.dayCostCurrent[format(day, 'yyyy-MM-dd')] ?? 0).toFixed(2)}`
                    : `${(totalsCache.dayHoursCurrent[format(day, 'yyyy-MM-dd')] ?? 0).toFixed(2)}h`
                  }
                </div>
                {/* All schedules day total (same location) - smaller font */}
                {(() => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const allSchedulesDayTotal = totalDisplayMode === 'cost'
                    ? (totalsCache.dayCostAll[dayStr] ?? 0)
                    : (totalsCache.dayHoursAll[dayStr] ?? 0);
                  const currentScheduleDayTotal = totalDisplayMode === 'cost'
                    ? (totalsCache.dayCostCurrent[dayStr] ?? 0)
                    : (totalsCache.dayHoursCurrent[dayStr] ?? 0);
                  
                  // Only show if all schedules total is different from current schedule total
                  if (Math.abs(allSchedulesDayTotal - currentScheduleDayTotal) > 0.01) {
                    return (
                      <div className="text-xs text-gray-600 mt-1">
                        {totalDisplayMode === 'cost'
                          ? `All: €${allSchedulesDayTotal.toFixed(2)}`
                          : `All: ${allSchedulesDayTotal.toFixed(2)}h`
                        }
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
            <div className="p-3 text-center border-l border-gray-200 bg-gray-200">
              <div className="flex items-center justify-center gap-2">
                <div className="font-semibold">
                  {totalDisplayMode === 'cost'
                    ? `€${totalsCache.grandCostCurrent.toFixed(2)}`
                    : `${totalsCache.grandHoursCurrent.toFixed(2)}h`
                  }
                </div>
                <button
                  onClick={() => setShowScheduleBreakdownModal(true)}
                  className="text-blue-600 hover:text-blue-800 transition-colors p-0.5 rounded hover:bg-blue-50"
                  title="View schedule breakdown"
                >
                  <Calculator className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
            </>
          )}
        </div>
      )}

      {/* Shift Form Modal */}
      {showShiftForm && editingShift && (() => {
        const employeePositions = getEmployeePositions(editingShift.employeeId);
        // Find "No Position" from the positions list
        const noPosition = positions.find(pos => pos.title.toLowerCase() === 'no position');
        // Combine employee positions with "No Position" if it exists and is not already in employee positions
        const employeePositionIds = new Set(employeePositions.map(pos => pos.id));
        const allAvailablePositions = noPosition && !employeePositionIds.has(noPosition.id)
          ? [...employeePositions, noPosition]
          : employeePositions;
        const formEmployee = employees.find((e) => e.id === editingShift.employeeId)
          ?? locationEmployees.find((e) => e.id === editingShift.employeeId);
        const allowPayExtraUi = formEmployee?.payroll?.allowAdditiveHourlyPay === true;
        const effectiveBreakMins = formData.breakUseGlobalSettings ? 0 : formData.unpaidBreakMins;
        const recalculatedHours = calculateHours(formData.startTime, formData.endTime, effectiveBreakMins);
        const breakHours = effectiveBreakMins / 60;
        const recalculatedTotalHours = formData.breakUseGlobalSettings
          ? recalculatedHours
          : (formData.breakTimePaid ? recalculatedHours + breakHours : recalculatedHours);
        const globalBreakMins = formData.startTime && formData.endTime
          ? calculateDefaultBreakMinutes(formData.startTime, formData.endTime)
          : null;
        const globalBreakPaid = breakTimeSettings?.isPaid ?? false;
        const globalBreakLabel = globalBreakPaid ? 'Paid' : 'Unpaid';
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl my-8">
            <h3 className="text-lg font-semibold mb-4">
              {editingShift.id === 0 ? 'Create Shift' : 'Edit Shift'}
            </h3>
            <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">

                {/* Start Time and End Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Time</label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => {
                        const newStartTime = e.target.value;
                        const newEndTime = formData.endTime;
                        
                        const isOverrideBreak = !formData.breakUseGlobalSettings;
                        let newBreakMins = formData.unpaidBreakMins || 0;
                        if (isOverrideBreak && newStartTime && newEndTime && !breakMinsManuallySet) {
                          newBreakMins = calculateDefaultBreakMinutes(newStartTime, newEndTime);
                        }
                        
                        const effectiveBreakMins = isOverrideBreak ? newBreakMins : 0;
                        const newHours = calculateHours(newStartTime, newEndTime, effectiveBreakMins);
                        const effectiveBreakHours = effectiveBreakMins / 60;
                        const newTotalHours = isOverrideBreak && formData.breakTimePaid
                          ? newHours + effectiveBreakHours
                          : newHours;
                        setFormData({ 
                          ...formData, 
                          startTime: newStartTime,
                          unpaidBreakMins: newBreakMins,
                          hours: newHours,
                          totalHours: newTotalHours
                        });
                      }}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Time</label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => {
                        const newEndTime = e.target.value;
                        const newStartTime = formData.startTime;
                        
                        const isOverrideBreak = !formData.breakUseGlobalSettings;
                        let newBreakMins = formData.unpaidBreakMins || 0;
                        if (isOverrideBreak && newStartTime && newEndTime && !breakMinsManuallySet) {
                          newBreakMins = calculateDefaultBreakMinutes(newStartTime, newEndTime);
                        }
                        
                        const effectiveBreakMins = isOverrideBreak ? newBreakMins : 0;
                        const newHours = calculateHours(newStartTime, newEndTime, effectiveBreakMins);
                        const effectiveBreakHours = effectiveBreakMins / 60;
                        const newTotalHours = isOverrideBreak && formData.breakTimePaid
                          ? newHours + effectiveBreakHours
                          : newHours;
                        setFormData({ 
                          ...formData, 
                          endTime: newEndTime,
                          unpaidBreakMins: newBreakMins,
                          hours: newHours,
                          totalHours: newTotalHours
                        });
                      }}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>

                {/* Break policy: Global Settings vs Override */}
                <div>
                  <label className="block text-sm font-medium mb-2">Break Policy</label>
                  <div className="flex flex-wrap gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="shiftBreakPolicy"
                        checked={formData.breakUseGlobalSettings}
                        onChange={() => {
                          const spanHours = calculateHours(formData.startTime, formData.endTime, 0);
                          setFormData({
                            ...formData,
                            breakUseGlobalSettings: true,
                            hours: spanHours,
                            totalHours: spanHours,
                          });
                        }}
                        className="text-blue-600"
                      />
                      <span className="text-sm">Global Settings</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="shiftBreakPolicy"
                        checked={!formData.breakUseGlobalSettings}
                        onChange={() => {
                          setBreakMinsManuallySet(false);
                          const newBreakMins = formData.startTime && formData.endTime
                            ? calculateDefaultBreakMinutes(formData.startTime, formData.endTime)
                            : 0;
                          const newHours = calculateHours(formData.startTime, formData.endTime, newBreakMins);
                          const breakHours = newBreakMins / 60;
                          const defaultPaid = breakTimeSettings?.isPaid ?? false;
                          setFormData({
                            ...formData,
                            breakUseGlobalSettings: false,
                            unpaidBreakMins: newBreakMins,
                            breakTimePaid: defaultPaid,
                            hours: newHours,
                            totalHours: defaultPaid ? newHours + breakHours : newHours,
                          });
                        }}
                        className="text-blue-600"
                      />
                      <span className="text-sm">Override</span>
                    </label>
                  </div>
                  {formData.breakUseGlobalSettings ? (
                    <>
                      <p className="text-xs text-neutral-500 mb-2">
                        From Settings → Break Policy (preview for this shift length)
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Break (minutes)</label>
                          <input
                            type="text"
                            value={globalBreakMins !== null ? globalBreakMins : '—'}
                            disabled
                            className="w-full px-3 py-2 border rounded bg-gray-50 text-neutral-700"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Break Time Paid</label>
                          <input
                            type="text"
                            value={globalBreakLabel}
                            disabled
                            className="w-full px-3 py-2 border rounded bg-gray-50 text-neutral-700"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Break (minutes)</label>
                        <input
                          type="number"
                          value={formData.unpaidBreakMins}
                          onChange={(e) => {
                            setBreakMinsManuallySet(true);
                            const breakMins = parseInt(e.target.value) || 0;
                            const newHours = calculateHours(formData.startTime, formData.endTime, breakMins);
                            const breakHours = breakMins / 60;
                            const newTotalHours = formData.breakTimePaid ? newHours + breakHours : newHours;
                            setFormData({ 
                              ...formData, 
                              unpaidBreakMins: breakMins,
                              hours: newHours,
                              totalHours: newTotalHours
                            });
                          }}
                          min="0"
                          className="w-full px-3 py-2 border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Break Time Paid</label>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              const newBreakTimePaid = !formData.breakTimePaid;
                              const breakHours = formData.unpaidBreakMins / 60;
                              const newTotalHours = newBreakTimePaid ? formData.hours + breakHours : formData.hours;
                              setFormData({ ...formData, breakTimePaid: newBreakTimePaid, totalHours: newTotalHours });
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              formData.breakTimePaid ? 'bg-blue-600' : 'bg-neutral-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                formData.breakTimePaid ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className="text-sm text-neutral-600">
                            {formData.breakTimePaid ? 'Paid' : 'Unpaid'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Hours and Total Hours (read-only) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Hours</label>
                    <input
                      type="text"
                      value={recalculatedHours.toFixed(2)}
                      disabled
                      className="w-full px-3 py-2 border rounded bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Total Hours</label>
                    <input
                      type="text"
                      value={recalculatedTotalHours.toFixed(2)}
                      disabled
                      className="w-full px-3 py-2 border rounded bg-gray-50"
                    />
                  </div>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium mb-1">Color</label>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-2">
                      {['#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#f97316', '#10b981'].map(color => (
                        <button
                          key={color}
                          onClick={() => setFormData({ ...formData, color })}
                          className={`w-8 h-8 rounded-full border-2 ${
                            formData.color === color ? 'border-neutral-900' : 'border-neutral-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value, isManualColorOverride: true })}
                        className="w-8 h-8 rounded border border-neutral-300 cursor-pointer"
                      />
                    </div>
                    {formData.color !== formData.originalColor && (
                      <label className="flex items-center gap-2 ml-4">
                        <input
                          type="checkbox"
                          checked={!formData.isManualColorOverride}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, color: formData.originalColor, isManualColorOverride: false });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Set to Default</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* End Time Display As */}
                <div>
                  <label className="block text-sm font-medium mb-1">End Time Display As</label>
                  <select
                    value={formData.displayOption}
                    onChange={(e) => setFormData({ ...formData, displayOption: e.target.value as 'time' | 'f' | 'close' })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="time">End Time</option>
                    <option value="f">F</option>
                    <option value="close">Close</option>
                  </select>
                </div>

                {/* Position */}
                <div className="relative position-dropdown-container">
                  <label className="block text-sm font-medium mb-1">Position</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowPositionDropdown(!showPositionDropdown)}
                      className="w-full px-3 py-2 border rounded text-left flex items-center justify-between bg-white"
                    >
                      <span className="flex items-center gap-2">
                        {formData.positionId ? (
                          <>
                            <div 
                              className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" 
                              style={{ backgroundColor: allAvailablePositions.find(p => p.id === formData.positionId)?.color || '#3b82f6' }}
                            />
                            <span>{allAvailablePositions.find(p => p.id === formData.positionId)?.title || 'Select Position'}</span>
                          </>
                        ) : (
                          <span className="text-gray-500">Select Position</span>
                        )}
                      </span>
                      <span className="text-gray-400">▼</span>
                    </button>
                    {showPositionDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        <div
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => {
                            setFormData({ 
                              ...formData, 
                              positionId: undefined,
                              color: formData.color,
                              originalColor: formData.color
                            });
                            setShowPositionDropdown(false);
                          }}
                        >
                          <span className="text-gray-500">Select Position</span>
                        </div>
                        {allAvailablePositions.map(pos => (
                          <div
                            key={pos.id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                            onClick={() => {
                              const newColor = colorMode === 'position' && pos.id
                                ? getShiftColorForMode(pos.id)
                                : formData.color;
                              setFormData({ 
                                ...formData, 
                                positionId: pos.id,
                                color: newColor,
                                originalColor: newColor
                              });
                              setShowPositionDropdown(false);
                            }}
                          >
                            <div 
                              className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" 
                              style={{ backgroundColor: pos.color }}
                            />
                            <span>{pos.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {allowPayExtraUi && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPayExtra}
                      onChange={(e) => setFormData({ ...formData, isPayExtra: e.target.checked })}
                      className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-neutral-700">Pay Extra</span>
                  </label>
                )}
                {allowPayExtraUi && (
                  <p className="text-xs text-neutral-500 -mt-2">
                    Additive matrix pay for these hours on top of salary. Prefer a sub-position when flexing into a second role.
                  </p>
                )}

                {/* Shift Group */}
                <div>
                  <label className="block text-sm font-medium mb-1">Group</label>
                  <div className="flex gap-2 items-center">
                    <select
                      value={formData.shiftGroupId ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          shiftGroupId: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      className="flex-1 px-3 py-2 border rounded"
                    >
                      <option value="">None</option>
                      {shiftGroupsForLocation.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      title="Add shift group"
                      onClick={() => {
                        setNewShiftGroupName('');
                        setShowCreateShiftGroupModal(true);
                      }}
                      className="p-2 border rounded hover:bg-blue-50 text-blue-600"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">
                    Used for &quot;Working with&quot; on My Schedules when set.
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Add notes..."
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => {
                      setShowShiftForm(false);
                      setEditingShift(null);
                      setBreakMinsManuallySet(false);
                      setShowPositionDropdown(false);
                    }}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  {editingShift.id !== 0 && (
                    <button
                      disabled={marketplaceByShiftId.has(editingShift.id)}
                      onClick={() => {
                        if (marketplaceByShiftId.has(editingShift.id)) return;
                        setShiftIdToDelete(editingShift.id);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        marketplaceByShiftId.has(editingShift.id)
                          ? marketplaceEditLockTitle(marketplaceByShiftId.get(editingShift.id)!)
                          : 'Delete shift'
                      }
                    >
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      Delete
                    </button>
                  )}
                  <button
                    onClick={handleSaveShift}
                    disabled={savingShift || (editingShift.id !== 0 && marketplaceByShiftId.has(editingShift.id))}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    title={
                      editingShift.id !== 0 && marketplaceByShiftId.has(editingShift.id)
                        ? marketplaceEditLockTitle(marketplaceByShiftId.get(editingShift.id)!)
                        : undefined
                    }
                  >
                    {savingShift && <Loader2 className="w-4 h-4 animate-spin" />}
                    {savingShift ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showCreateShiftGroupModal && editingShift && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-3">New Shift Group</h3>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <input
              type="text"
              value={newShiftGroupName}
              onChange={(e) => setNewShiftGroupName(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-4"
              placeholder="e.g. Ward A Day"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreateShiftGroupModal(false)}
                className="px-3 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={creatingShiftGroup || !newShiftGroupName.trim()}
                onClick={async () => {
                  if (!newShiftGroupName.trim() || !editingShift.locationId) return;
                  setCreatingShiftGroup(true);
                  try {
                    const created = await shiftGroupApi.create({
                      name: newShiftGroupName.trim(),
                      locationIds: [editingShift.locationId],
                    });
                    setShiftGroupsForLocation((prev) =>
                      [...prev.filter((g) => g.id !== created.id), created].sort((a, b) =>
                        a.name.localeCompare(b.name)
                      )
                    );
                    setFormData((prev) => ({ ...prev, shiftGroupId: created.id }));
                    setShowCreateShiftGroupModal(false);
                    setNewShiftGroupName('');
                  } catch (error: any) {
                    console.error('Error creating shift group:', error);
                    const message =
                      error?.response?.data?.message ||
                      error?.response?.data?.title ||
                      error?.message ||
                      'Failed to create shift group';
                    alert(typeof message === 'string' ? message : 'Failed to create shift group');
                  } finally {
                    setCreatingShiftGroup(false);
                  }
                }}
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingShiftGroup ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Working time / rest soft-warning modal (same pattern as unlock/delete) */}
      {restRuleWarningModal && (
        <RestRuleWarningsModal
          warnings={restRuleWarningModal.warnings}
          onClose={() => setRestRuleWarningModal(null)}
        />
      )}

      {/* Unlock Published Schedule Warning Modal */}
      {showUnlockWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center gap-3 p-6 border-b">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <Unlock className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Unlock Published Schedule</h2>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Shift is published and locked. Editing the published shift will override timecard data.
              </p>
              
              {/* Warning Box */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <p className="text-orange-800 text-sm">
                  Once modified, the schedule will be marked as "Changed" and will need to be republished.
                </p>
              </div>

              <p className="text-gray-700 mb-6">Do you want to unlock for editing?</p>

              {/* Buttons */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleUnlockCancel}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnlockConfirm}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  Yes, Unlock for Editing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Shift Confirmation Dialog */}
      {shiftIdToDelete !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center gap-3 p-6 border-b">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Delete Shift</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                Are you sure you want to delete this shift? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShiftIdToDelete(null)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => shiftIdToDelete !== null && handleDeleteShift(shiftIdToDelete)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPlaceholderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Placeholder seats</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Named seats for {selectedSchedule?.name || 'this schedule'}. Use them when the person is not known yet.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPlaceholderModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <form onSubmit={savePlaceholder} className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={placeholderDraft}
                  onChange={(e) => setPlaceholderDraft(e.target.value)}
                  placeholder={editingPlaceholder ? 'Rename placeholder' : 'e.g. Lucan Medical Center GP'}
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={savingPlaceholder}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingPlaceholder ? 'Update' : 'Add'}
                </button>
                {editingPlaceholder && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingPlaceholder(null);
                      setPlaceholderDraft('');
                    }}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    Cancel
                  </button>
                )}
              </form>
              {placeholderList.length === 0 ? (
                <p className="text-sm text-gray-500">No placeholders on this schedule yet.</p>
              ) : (
                <>
                  <p className="text-xs text-gray-500 mb-2">
                    Seats are hidden on the calendar until you show them.
                  </p>
                  <ul className="divide-y border rounded-md">
                    {placeholderList.map((row) => {
                      const shown = visiblePlaceholderIds.includes(row.id);
                      return (
                        <li key={row.id} className="flex items-center justify-between gap-2 px-3 py-2">
                          <div>
                            <div className="font-medium text-sm">{row.name}</div>
                            {row.isUsed && (
                              <div className="text-xs text-amber-700">Used on shifts — rename/delete locked</div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => setPlaceholderVisibility(row.id, !shown)}
                              className={`p-2 rounded ${shown ? 'text-indigo-700 hover:bg-indigo-50' : 'text-neutral-400 hover:text-indigo-700 hover:bg-gray-50'}`}
                              title={shown ? 'Hide on calendar' : 'Show on calendar'}
                            >
                              {shown ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                            <button
                              type="button"
                              disabled={row.isUsed}
                              onClick={() => {
                                setEditingPlaceholder(row);
                                setPlaceholderDraft(row.name);
                              }}
                              className="p-2 text-neutral-600 hover:text-blue-600 disabled:opacity-40"
                              title={row.isUsed ? 'In use' : 'Rename'}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={row.isUsed}
                              onClick={() => void deletePlaceholder(row)}
                              className="p-2 text-neutral-600 hover:text-red-600 disabled:opacity-40"
                              title={row.isUsed ? 'In use' : 'Delete'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showExpectedCountsModal && selectedSchedule && debouncedSelectedLocationId && (
        <ScheduleExpectedCountsWeekModal
          scheduleId={selectedSchedule.id}
          scheduleName={selectedSchedule.name}
          locationId={debouncedSelectedLocationId}
          weekStart={weekStartStr}
          weekEnd={weekEndStr}
          onClose={() => setShowExpectedCountsModal(false)}
          onSaved={(week) =>
            setWeekExpectedCounts(
              normalizeWeekExpectedCounts(
                week,
                selectedSchedule.id,
                debouncedSelectedLocationId,
                weekStartStr
              )
            )
          }
        />
      )}

      {assignSeat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Assign person</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Move this week’s shifts on “{assignSeat.name}” to a real employee.
                  {assignSeatThisWeekCount > 0
                    ? ` ${assignSeatThisWeekCount} shift${assignSeatThisWeekCount === 1 ? '' : 's'} this week.`
                    : ' No shifts on this seat this week.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAssignSeat(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <input
                type="text"
                value={assignQuery}
                onChange={(e) => setAssignQuery(e.target.value)}
                placeholder="Search people on this schedule..."
                className="w-full px-3 py-2 mb-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="border rounded-md max-h-72 overflow-y-auto divide-y">
                {assignCandidates.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-gray-500">No matching people.</p>
                ) : (
                  assignCandidates.map((emp) => (
                    <label
                      key={emp.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                        assignEmployeeId === emp.id ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="assign-seat-employee"
                        checked={assignEmployeeId === emp.id}
                        onChange={() => setAssignEmployeeId(emp.id)}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-800">
                          {emp.firstName} {emp.lastName}
                          {emp.isActive === false && (
                            <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600 bg-neutral-100 border border-neutral-200 px-1.5 py-0.5 rounded">
                              Inactive
                            </span>
                          )}
                        </span>
                        <span className="block text-xs text-gray-500 truncate">{emp.email}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                type="button"
                onClick={() => setAssignSeat(null)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={assigningSeat || !assignEmployeeId || assignSeatThisWeekCount === 0}
                onClick={() => void confirmAssignSeat()}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {assigningSeat && <Loader2 className="w-4 h-4 animate-spin" />}
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Week Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Select Week to View Records</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Choose a week to display all shifts/records for that period.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportWeekPickerOpen(false);
                  setImportWeekStart(null);
                  setImportShifts([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex-1 overflow-y-auto">
              {/* Week Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Week
                </label>
                {importWeekPickerOpen ? (
                  <WeekPickerCalendar
                    value={importWeekStart ? toLocalDateString(importWeekStart) : ''}
                    onChange={(ymd) => {
                      if (ymd) void handleWeekSelectChange(parseLocalDate(ymd));
                      setImportWeekPickerOpen(false);
                    }}
                    onClose={() => setImportWeekPickerOpen(false)}
                    weekStartsOn={weekStartsOn}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setImportWeekPickerOpen(true)}
                    className="flex w-full items-center justify-between rounded border border-gray-300 px-4 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <span className={importWeekStart ? 'text-gray-900' : 'text-gray-500'}>
                      {importWeekStart
                        ? formatCompanyDateRange(importWeekStart, endOfWeek(importWeekStart, { weekStartsOn }))
                        : 'Select a week...'}
                    </span>
                    <Calendar className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
                  </button>
                )}
              </div>

              {/* Week Summary */}
              {importWeekStart && importShifts.length > 0 && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">Week Summary</span>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-700">
                          {importSummary.totalHours.toFixed(2)} hrs
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {importSummary.recordCount} records
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Shift Records */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Shift Records ({importSummary.recordCount} total)
                    </h3>
                    <div className="space-y-3">
                      {importShifts.map((shift, index) => {
                        const shiftDate = parseLocalDateTime(shift.startTime);
                        const startTime = format(parseLocalDateTime(shift.startTime), 'HH:mm');
                        const endTime = format(parseLocalDateTime(shift.endTime), 'HH:mm');
                        const totalHours = getShiftTotalHours(shift);
                        const employee = employees.find(e => e.id === shift.employeeId);
                        const position = positions.find(p => p.id === shift.positionId);
                        
                        return (
                          <div key={shift.id || index} className="border-b border-gray-200 pb-3 last:border-b-0">
                            <div className="flex items-center justify-between">
                              {/* Left Side - Employee Info */}
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="text-blue-700 font-semibold text-sm">
                                    {employee ? `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}` : 'N/A'}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-800">
                                    {employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unknown Employee' : 'Unknown Employee'}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {position?.title || 'No Position'}
                                  </div>
                                </div>
                              </div>

                              {/* Right Side - Shift Details */}
                              <div className="text-right">
                                <div className="font-semibold text-gray-800">
                                  {startTime} - {endTime}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {format(shiftDate, 'EEE, d MMM yyyy')}
                                </div>
                                <div className="text-sm text-gray-600">
                                  Hours: {totalHours.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

            </div>

            {/* Footer */}
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportWeekPickerOpen(false);
                  setImportWeekStart(null);
                  setImportShifts([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
              >
                Close
              </button>
              {importShifts.length > 0 && (
                <button
                  onClick={() => setShowImportConfirm(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Import {importSummary.recordCount} Records
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Confirmation Modal */}
      {showImportConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Confirm Import
              </h3>
              <p className="text-gray-700 mb-6">
                This will replace all records in the current week ({formatCompanyDateRange(weekStart, endOfWeek(weekStart, { weekStartsOn }))}) with {importSummary.recordCount} records from the selected week. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  disabled={importWeekInProgress}
                  onClick={() => setShowImportConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={importWeekInProgress}
                  onClick={handleImportConfirm}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[10.5rem]"
                >
                  {importWeekInProgress ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
                      <span>Importing…</span>
                    </>
                  ) : (
                    'Confirm Import'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showScheduleFileImportModal && selectedLocationId && selectedScheduleId != null && (
        <ImportScheduleFromFileModal
          open={showScheduleFileImportModal}
          onClose={() => setShowScheduleFileImportModal(false)}
          locationId={selectedLocationId}
          scheduleId={selectedScheduleId}
          allowedWeekStartDate={format(weekStart, 'yyyy-MM-dd')}
          allowedWeekEndDate={format(weekEnd, 'yyyy-MM-dd')}
          existingShiftCount={currentScheduleWeekShiftCount}
          onImportCommitted={async (result) => {
            if (result?.placeholderIds?.length && selectedScheduleId) {
              const current = visiblePlaceholderIdsRef.current;
              const next = current.concat(
                result.placeholderIds.filter((id) => !current.includes(id))
              );
              visiblePlaceholderIdsRef.current = next;
              setVisiblePlaceholderIds(next);
              saveVisiblePlaceholderIds(selectedScheduleId, next);
            }
            await reloadWeekView();
            if (selectedLocationId && selectedScheduleId) {
              const weekStartStr = format(weekStart, 'yyyy-MM-dd');
              const updatedStatus = await schedulePublicationStatusApi.get(
                selectedLocationId,
                selectedScheduleId,
                weekStartStr
              );
              setPublicationStatus(updatedStatus);
            }
          }}
        />
      )}

      {showBulkDeleteScheduleShiftsModal && selectedLocationId && selectedScheduleId != null && (
        <BulkDeleteScheduleShiftsModal
          open={showBulkDeleteScheduleShiftsModal}
          onClose={() => setShowBulkDeleteScheduleShiftsModal(false)}
          locationId={selectedLocationId}
          scheduleId={selectedScheduleId}
          weekStartDate={format(weekStart, 'yyyy-MM-dd')}
          shifts={shifts.filter(isCurrentScheduleShift)}
          onDeleted={async () => {
            await reloadWeekView();
            if (selectedLocationId && selectedScheduleId) {
              const weekStartStr = format(weekStart, 'yyyy-MM-dd');
              const updatedStatus = await schedulePublicationStatusApi.get(
                selectedLocationId,
                selectedScheduleId,
                weekStartStr
              );
              setPublicationStatus(updatedStatus);
            }
          }}
        />
      )}

      {/* Sort Employees Modal */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">Sort Employees</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Use the dropdown to select employees for each position, or drag and drop rows to reorder them.
                </p>
              </div>
              <button
                onClick={() => setShowSortModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search employees for selection..."
                  className="w-full px-3 py-2 pl-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
              </div>
            </div>

            {/* Employee List */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredEmployees.filter((employee) => !employee.isPlaceholder).map((employee, index) => {
                const sort = employeeSortOrder.find(o => o.employeeId === employee.id);
                const displayOrder = sort ? sort.sortOrder : index + 1;
                const initials = `${employee.firstName[0] || ''}${employee.lastName[0] || ''}`.toUpperCase();
                
                return (
                  <div
                    key={employee.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', employee.id.toString());
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const draggedEmployeeId = parseInt(e.dataTransfer.getData('text/plain'));
                      if (draggedEmployeeId !== employee.id) {
                        const newOrder = [...employeeSortOrder];
                        const draggedIndex = newOrder.findIndex(o => o.employeeId === draggedEmployeeId);
                        const targetIndex = newOrder.findIndex(o => o.employeeId === employee.id);
                        
                        if (draggedIndex !== -1 && targetIndex !== -1) {
                          const [removed] = newOrder.splice(draggedIndex, 1);
                          newOrder.splice(targetIndex, 0, removed);
                          // Reassign sort orders
                          newOrder.forEach((item, idx) => {
                            item.sortOrder = idx + 1;
                          });
                          setEmployeeSortOrder(newOrder);
                        } else {
                          // If dragged item not in sort order, add it
                          if (draggedIndex === -1) {
                            const draggedEmp = filteredEmployees.find(e => e.id === draggedEmployeeId);
                            if (draggedEmp) {
                              newOrder.splice(targetIndex, 0, { employeeId: draggedEmployeeId, sortOrder: targetIndex + 1 });
                              newOrder.forEach((item, idx) => {
                                item.sortOrder = idx + 1;
                              });
                              setEmployeeSortOrder(newOrder);
                            }
                          }
                        }
                      }
                    }}
                    className="flex items-center gap-3 p-3 border rounded-lg mb-2 cursor-move hover:bg-gray-50"
                  >
                    <GripVertical className="w-5 h-5 text-gray-400" />
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-sm">
                      {displayOrder}
                    </div>
                    <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center font-semibold text-sm">
                      {initials}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{employee.firstName} {employee.lastName}</div>
                    </div>
                    <span className="text-gray-400">▼</span>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setShowSortModal(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedLocationId && selectedScheduleId) {
                    try {
                      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
                      await employeeSortOrderApi.save(selectedLocationId, selectedScheduleId, weekStartStr, employeeSortOrder);
                      setShowSortModal(false);
                    } catch (error) {
                      console.error('Error saving sort order:', error);
                      alert('Failed to save sort order. Please try again.');
                    }
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Sort Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cost Breakdown Modal */}
      {showCostBreakdownModal && selectedEmployeeForBreakdown && (() => {
        const payRecord = employeePayRecords[selectedEmployeeForBreakdown.id];
        const formulaInfo = payRecord ? getFormulaExplanation(payRecord) : null;
        const shiftBreakdown = getEmployeeShiftBreakdown(selectedEmployeeForBreakdown.id);
        const totalCost = getEmployeeTotalCost(selectedEmployeeForBreakdown.id);

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Cost Breakdown - {selectedEmployeeForBreakdown.firstName} {selectedEmployeeForBreakdown.lastName}
                </h3>
                <button
                  onClick={() => {
                    setShowCostBreakdownModal(false);
                    setSelectedEmployeeForBreakdown(null);
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!payRecord ? (
                <div className="text-center py-8 text-gray-500">
                  No PayRecord found for this employee.
                </div>
              ) : (
                <div className="space-y-6">
                  {/* PayRecord Details */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">PayRecord Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Payment Type:</span> {payRecord.paymentType?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Payment Frequency:</span> {payRecord.paymentFrequency?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Base Rate:</span> €{payRecord.baseRate?.toFixed(2) || '0.00'}
                      </div>
                      <div>
                        <span className="font-medium">Contracted Hours (per week):</span> {payRecord.contractedHours?.toFixed(2) || '0.00'}
                      </div>
                      {payRecord.assignLaborCostBy && (
                        <div>
                          <span className="font-medium">Assign Labor Cost By:</span> {payRecord.assignLaborCostBy.name}
                        </div>
                      )}
                      {payRecord.workingDaysPerWeek && (
                        <div>
                          <span className="font-medium">Working Days per Week:</span> {payRecord.workingDaysPerWeek}
                        </div>
                      )}
                      {payRecord.dailyRate && (
                        <div>
                          <span className="font-medium">Daily Rate:</span> €{payRecord.dailyRate.toFixed(2)}
                        </div>
                      )}
                      {payRecord.payRecordWorkingDays && payRecord.payRecordWorkingDays.length > 0 && (
                        <div>
                          <span className="font-medium">Preset Days:</span> {payRecord.payRecordWorkingDays.map(prwd => prwd.workingDay?.name).filter(Boolean).join(', ')}
                        </div>
                      )}
                      {payRecord.paymentType?.name === 'Project' && payRecord.projectStartDate && payRecord.projectEndDate && (
                        <>
                          <div>
                            <span className="font-medium">Project Start Date:</span> {formatCompanyDate(payRecord.projectStartDate)}
                          </div>
                          <div>
                            <span className="font-medium">Project End Date:</span> {formatCompanyDate(payRecord.projectEndDate)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Formula Explanation */}
                  {formulaInfo && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-3">Calculation Formula</h4>
                      <div className="bg-gray-50 p-3 rounded mb-2">
                        <div className="font-mono text-sm font-semibold mb-1">{formulaInfo.formula}</div>
                        <div className="text-sm text-gray-600">{formulaInfo.description}</div>
                      </div>
                      <div className="space-y-1 text-sm">
                        {Object.entries(formulaInfo.values).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="font-medium">{key}:</span>
                            <span>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shift Breakdown */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">Shift Breakdown for Week</h4>
                    {shiftBreakdown.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">No shifts scheduled for this week.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">End Time</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Break (mins)</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Break Paid</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hourly Rate</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {shiftBreakdown.map((item, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">{format(item.date, 'EEE')}, {formatCompanyDate(item.date)}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">{format(parseLocalDateTime(item.shift.startTime), 'HH:mm')}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">{format(parseLocalDateTime(item.shift.endTime), 'HH:mm')}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right">{item.breakMins}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-center">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    item.breakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {item.breakPaid ? 'Paid' : 'Unpaid'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right">{item.shiftHours.toFixed(2)}h</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right">€{item.hourlyRate.toFixed(2)}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-semibold">€{item.cost.toFixed(2)}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 font-semibold">
                              <td colSpan={7} className="px-3 py-2 text-right text-sm">Total:</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right">€{totalCost.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Schedule Breakdown Modal */}
      {showScheduleBreakdownModal && selectedLocationId && (() => {
        const breakdownShifts = weekView
          ? weekView.locationShifts.map(normalizeShift)
          : locationShifts;
        const breakdownPayRecords = weekView?.locationPayRecords ?? locationPayRecords;

        // Get all unique schedule IDs from shifts for this location
        const scheduleIdsFromShifts = new Set(
          breakdownShifts.map(shift => shift.scheduleId).filter(id => id !== undefined && id !== null)
        );

        // Get all schedules that either:
        // 1. Have locationIds including selectedLocationId, OR
        // 2. Have shifts in the locationShifts array
        const locationSchedules = schedules.filter(s => 
          (s.locationIds && s.locationIds.includes(selectedLocationId)) ||
          scheduleIdsFromShifts.has(s.id)
        );

        // Calculate breakdown per schedule/day
        const scheduleBreakdown: Record<number, {
          scheduleName: string;
          hourly: Record<string, number>; // day -> cost
          salary: Record<string, number>; // day -> cost
        }> = {};

        // Initialize breakdown structure for all schedules that have shifts
        // Also include schedules filtered by locationIds to show empty rows if needed
        const allSchedulesToShow = new Set([
          ...locationSchedules.map(s => s.id),
          ...Array.from(scheduleIdsFromShifts)
        ]);

        allSchedulesToShow.forEach(scheduleId => {
          const schedule = schedules.find(s => s.id === scheduleId);
          if (schedule) {
            scheduleBreakdown[scheduleId] = {
              scheduleName: schedule.name,
              hourly: {},
              salary: {},
            };
            weekDays.forEach(day => {
              const dayStr = format(day, 'yyyy-MM-dd');
              scheduleBreakdown[scheduleId].hourly[dayStr] = 0;
              scheduleBreakdown[scheduleId].salary[dayStr] = 0;
            });
          } else {
            // Schedule not found in schedules list, but has shifts - use scheduleId as name
            scheduleBreakdown[scheduleId] = {
              scheduleName: `Schedule ${scheduleId}`,
              hourly: {},
              salary: {},
            };
            weekDays.forEach(day => {
              const dayStr = format(day, 'yyyy-MM-dd');
              scheduleBreakdown[scheduleId].hourly[dayStr] = 0;
              scheduleBreakdown[scheduleId].salary[dayStr] = 0;
            });
          }
        });

        // Helper function to calculate shift cost (similar to getShiftLaborCost but uses payRecord parameter)
        const calculateShiftCost = (shift: Shift, payRecord: PayRecord): number => {
          if (!payRecord) return 0;
          
          const hourlyRate = calculateHourlyRateFromPayRecord(payRecord);
          const shiftHours = getShiftPaidHoursForCost(shift);
          
          return hourlyRate * shiftHours;
        };

        // Calculate costs per shift
        breakdownShifts.forEach(shift => {
          const dayStr = format(parseISO(shift.startTime), 'yyyy-MM-dd');
          const payRecord = breakdownPayRecords[shift.employeeId];
          
          if (!payRecord) return;
          
          // Initialize breakdown for this schedule if it doesn't exist
          if (!scheduleBreakdown[shift.scheduleId]) {
            const schedule = schedules.find(s => s.id === shift.scheduleId);
            scheduleBreakdown[shift.scheduleId] = {
              scheduleName: schedule?.name || `Schedule ${shift.scheduleId}`,
              hourly: {},
              salary: {},
            };
            weekDays.forEach(day => {
              const dayStr = format(day, 'yyyy-MM-dd');
              scheduleBreakdown[shift.scheduleId].hourly[dayStr] = 0;
              scheduleBreakdown[shift.scheduleId].salary[dayStr] = 0;
            });
          }
          
          const shiftCost = calculateShiftCost(shift, payRecord);
          const paymentTypeName = payRecord.paymentType?.name || '';
          
          // Categorize as hourly or salary (non-hourly)
          if (paymentTypeName === 'Hourly') {
            scheduleBreakdown[shift.scheduleId].hourly[dayStr] = 
              (scheduleBreakdown[shift.scheduleId].hourly[dayStr] || 0) + shiftCost;
          } else {
            scheduleBreakdown[shift.scheduleId].salary[dayStr] = 
              (scheduleBreakdown[shift.scheduleId].salary[dayStr] || 0) + shiftCost;
          }
        });

        // Calculate totals
        const weeklyTotals = {
          hourly: 0,
          salary: 0,
        };
        
        Object.values(scheduleBreakdown).forEach(schedule => {
          Object.values(schedule.hourly).forEach(cost => weeklyTotals.hourly += cost);
          Object.values(schedule.salary).forEach(cost => weeklyTotals.salary += cost);
        });

        // Schedules in summary order (descending by total cost) - used for table and charts
        const scheduleCostsOrdered = Object.entries(scheduleBreakdown)
          .map(([scheduleIdStr, breakdown]) => {
            const scheduleHourly = Object.values(breakdown.hourly).reduce((s, c) => s + c, 0);
            const scheduleSalary = Object.values(breakdown.salary).reduce((s, c) => s + c, 0);
            const cost = scheduleHourly + scheduleSalary;
            return {
              scheduleId: scheduleIdStr,
              scheduleName: breakdown.scheduleName,
              cost,
              hourly: scheduleHourly,
              salary: scheduleSalary,
              breakdown,
            };
          })
          .filter(({ cost }) => cost > 0)
          .sort((a, b) => b.cost - a.cost);

        // Per-day max for bar scale (so visual bars are comparable across cells)
        const dayMaxByDay: Record<string, number> = {};
        weekDays.forEach(day => {
          const dayStr = format(day, 'yyyy-MM-dd');
          let max = 0;
          scheduleCostsOrdered.forEach(({ breakdown }) => {
            const dayTotal = (breakdown.hourly[dayStr] || 0) + (breakdown.salary[dayStr] || 0);
            if (dayTotal > max) max = dayTotal;
          });
          dayMaxByDay[dayStr] = Math.max(max, 1);
        });

        // Format date range for title
        const dateRangeStr = formatCompanyDateRange(weekStart, weekEnd).replace(' – ', ' to ');

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-7xl my-8 max-h-[90vh] overflow-y-auto flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  <h3 className="text-lg font-semibold">
                    Weekly Schedule Breakdown - {dateRangeStr}
                  </h3>
                </div>
                <button
                  onClick={() => setShowScheduleBreakdownModal(false)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {loadingBreakdown ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  <span className="ml-3 text-gray-600">Loading breakdown data...</span>
                </div>
              ) : (
              <>
              {/* Hidden: original numeric data table (can be re-enabled later) */}
              {false && (
              <div className="overflow-x-auto mb-6" style={{ maxHeight: 'calc(90vh - 300px)', overflowY: 'auto' }}>
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border-r border-gray-300">Schedule</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border-r border-gray-300">Type</th>
                      {weekDays.map(day => (
                        <th key={day.toISOString()} className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-300">
                          {format(day, 'EEE')}, {formatCompanyDate(day)}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(scheduleBreakdown)
                      .sort(([idA], [idB]) => {
                        const nameA = scheduleBreakdown[Number(idA)].scheduleName;
                        const nameB = scheduleBreakdown[Number(idB)].scheduleName;
                        return nameA.localeCompare(nameB);
                      })
                      .map(([scheduleIdStr, breakdown]) => {
                        const scheduleId = Number(scheduleIdStr);
                        const scheduleHourlyTotal = Object.values(breakdown.hourly).reduce((sum, cost) => sum + cost, 0);
                        const scheduleSalaryTotal = Object.values(breakdown.salary).reduce((sum, cost) => sum + cost, 0);
                        const scheduleTotal = scheduleHourlyTotal + scheduleSalaryTotal;
                        if (scheduleTotal === 0) return null;
                        return (
                          <React.Fragment key={scheduleId}>
                            <tr className="bg-gray-50">
                              <td className="px-4 py-2 font-semibold border-r border-gray-300">{breakdown.scheduleName}</td>
                              <td className="px-4 py-2 text-sm border-r border-gray-300">Hourly</td>
                              {weekDays.map(day => {
                                const dayStr = format(day, 'yyyy-MM-dd');
                                const cost = breakdown.hourly[dayStr] || 0;
                                return (
                                  <td key={dayStr} className="px-3 py-2 text-center text-sm border-r border-gray-300">
                                    {cost > 0 ? `€${cost.toFixed(2)}` : '€0.00'}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2 text-center text-sm font-semibold">€{scheduleHourlyTotal.toFixed(2)}</td>
                            </tr>
                            <tr className="bg-gray-50">
                              <td className="px-4 py-2 border-r border-gray-300"></td>
                              <td className="px-4 py-2 text-sm border-r border-gray-300">Salary</td>
                              {weekDays.map(day => {
                                const dayStr = format(day, 'yyyy-MM-dd');
                                const cost = breakdown.salary[dayStr] || 0;
                                return (
                                  <td key={dayStr} className="px-3 py-2 text-center text-sm border-r border-gray-300">
                                    {cost > 0 ? `€${cost.toFixed(2)}` : '€0.00'}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2 text-center text-sm font-semibold">€{scheduleSalaryTotal.toFixed(2)}</td>
                            </tr>
                            <tr className="bg-blue-100 border-t-2 border-blue-300">
                              <td className="px-4 py-2 font-semibold border-r border-gray-300">{breakdown.scheduleName}</td>
                              <td className="px-4 py-2 font-semibold border-r border-gray-300">Total</td>
                              {weekDays.map(day => {
                                const dayStr = format(day, 'yyyy-MM-dd');
                                const dayTotal = (breakdown.hourly[dayStr] || 0) + (breakdown.salary[dayStr] || 0);
                                return (
                                  <td key={dayStr} className="px-3 py-2 text-center text-sm font-semibold border-r border-gray-300">
                                    {dayTotal > 0 ? `€${dayTotal.toFixed(2)}` : '€0.00'}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2 text-center text-sm font-bold text-blue-700">€{scheduleTotal.toFixed(2)}</td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    <tr className="bg-gray-200 font-bold border-t-2 border-gray-400">
                      <td className="px-4 py-3 border-r border-gray-300 font-semibold">Grand Total</td>
                      <td className="px-4 py-3 border-r border-gray-300 font-semibold">Total</td>
                      {weekDays.map(day => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const dayHourlyTotal = Object.values(scheduleBreakdown).reduce((sum, schedule) => sum + (schedule.hourly[dayStr] || 0), 0);
                        const daySalaryTotal = Object.values(scheduleBreakdown).reduce((sum, schedule) => sum + (schedule.salary[dayStr] || 0), 0);
                        const dayGrandTotal = dayHourlyTotal + daySalaryTotal;
                        return (
                          <td key={dayStr} className="px-3 py-3 text-center text-sm font-semibold border-r border-gray-300">
                            {dayGrandTotal > 0 ? `€${dayGrandTotal.toFixed(2)}` : '€0.00'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center text-sm font-semibold">€{(weeklyTotals.hourly + weeklyTotals.salary).toFixed(2)}</td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td className="px-4 py-2 border-r border-gray-300"></td>
                      <td className="px-4 py-2 text-sm font-semibold border-r border-gray-300">Hourly</td>
                      {weekDays.map(day => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const dayHourlyTotal = Object.values(scheduleBreakdown).reduce((sum, schedule) => sum + (schedule.hourly[dayStr] || 0), 0);
                        return (
                          <td key={dayStr} className="px-3 py-2 text-center text-sm font-semibold border-r border-gray-300">
                            {dayHourlyTotal > 0 ? `€${dayHourlyTotal.toFixed(2)}` : '€0.00'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-2 text-center text-sm font-semibold">€{weeklyTotals.hourly.toFixed(2)}</td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td className="px-4 py-2 border-r border-gray-300"></td>
                      <td className="px-4 py-2 text-sm font-semibold border-r border-gray-300">Salary</td>
                      {weekDays.map(day => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const daySalaryTotal = Object.values(scheduleBreakdown).reduce((sum, schedule) => sum + (schedule.salary[dayStr] || 0), 0);
                        return (
                          <td key={dayStr} className="px-3 py-2 text-center text-sm font-semibold border-r border-gray-300">
                            {daySalaryTotal > 0 ? `€${daySalaryTotal.toFixed(2)}` : '€0.00'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-2 text-center text-sm font-semibold">€{weeklyTotals.salary.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}

              {/* Hourly / Salary breakup per day by visualization (same order as summary) */}
              <div className="overflow-x-auto mb-6">
                <div className="flex gap-2 mb-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#2563eb]" /> Hourly</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#0d9488]" /> Salary</span>
                </div>
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border-r border-gray-300">Schedule</th>
                      {weekDays.map(day => (
                        <th key={day.toISOString()} className="px-2 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-300 min-w-[100px]">
                          {format(day, 'EEE')}, {formatCompanyDate(day)}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase min-w-[80px]">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {scheduleCostsOrdered.length === 0 ? (
                      <tr>
                        <td colSpan={weekDays.length + 2} className="px-4 py-8 text-center text-gray-500">
                          No schedule costs this week.
                        </td>
                      </tr>
                    ) : (
                      scheduleCostsOrdered.map(({ scheduleId, scheduleName, cost, breakdown }) => (
                        <tr key={scheduleId} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-semibold border-r border-gray-300 align-top pt-3">
                            {scheduleName}
                          </td>
                          {weekDays.map(day => {
                            const dayStr = format(day, 'yyyy-MM-dd');
                            const dayHourly = breakdown.hourly[dayStr] || 0;
                            const daySalary = breakdown.salary[dayStr] || 0;
                            const dayTotal = dayHourly + daySalary;
                            const maxDay = dayMaxByDay[dayStr] || 1;
                            const barWidth = 80;
                            const totalBarW = (dayTotal / maxDay) * barWidth;
                            const hourlyW = dayTotal > 0 ? (dayHourly / dayTotal) * totalBarW : 0;
                            const salaryW = dayTotal > 0 ? (daySalary / dayTotal) * totalBarW : 0;
                            return (
                              <td key={dayStr} className="px-2 py-2 border-r border-gray-300 align-top">
                                <div className="flex flex-col items-center gap-0.5">
                                  <div
                                    className="flex h-6 rounded overflow-hidden bg-gray-100 border border-gray-200"
                                    style={{ width: barWidth + 4, minWidth: barWidth + 4 }}
                                    title={`Hourly: €${dayHourly.toFixed(2)} | Salary: €${daySalary.toFixed(2)}`}
                                  >
                                    {dayHourly > 0 && (
                                      <div
                                        className="h-full bg-[#2563eb] min-w-[2px]"
                                        style={{ width: Math.max(hourlyW, 2) }}
                                      />
                                    )}
                                    {daySalary > 0 && (
                                      <div
                                        className="h-full bg-[#0d9488] min-w-[2px]"
                                        style={{ width: Math.max(salaryW, 2) }}
                                      />
                                    )}
                                  </div>
                                  <span className="text-xs font-medium text-gray-700 tabular-nums">
                                    {dayTotal > 0 ? `€${dayTotal.toFixed(2)}` : '—'}
                                  </span>
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-4 py-2 text-center font-semibold text-gray-800 align-top pt-3">
                            €{cost.toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Cost breakdown: horizontal bar chart (left) + circle chart (right) - Sticky at bottom */}
              {(() => {
                const total = weeklyTotals.hourly + weeklyTotals.salary;
                const hourly = weeklyTotals.hourly;
                const salary = weeklyTotals.salary;
                const scheduleCosts = scheduleCostsOrdered.map(({ scheduleId, scheduleName, cost, hourly, salary }) => ({
                  scheduleId,
                  name: scheduleName,
                  cost,
                  hourly,
                  salary,
                }));
                const maxScheduleCost = Math.max(...scheduleCosts.map(s => s.cost), 1);
                const barChartWidth = 240;
                const barHeight = 22;
                const barGap = 4;
                const labelWidth = 100;
                const size = 220;
                const cx = size / 2;
                const cy = size / 2;
                const outerR = 72;
                const innerR = 44;
                const deg = (n: number) => (n / (total || 1)) * 360;
                const toRad = (d: number) => (d - 90) * (Math.PI / 180);
                const segmentPath = (startDeg: number, endDeg: number) => {
                  const start = toRad(startDeg);
                  const end = toRad(endDeg);
                  const x1 = cx + outerR * Math.cos(start);
                  const y1 = cy + outerR * Math.sin(start);
                  const x2 = cx + outerR * Math.cos(end);
                  const y2 = cy + outerR * Math.sin(end);
                  const x3 = cx + innerR * Math.cos(end);
                  const y3 = cy + innerR * Math.sin(end);
                  const x4 = cx + innerR * Math.cos(start);
                  const y4 = cy + innerR * Math.sin(start);
                  const large = endDeg - startDeg > 180 ? 1 : 0;
                  return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 ${large} 0 ${x4} ${y4} Z`;
                };
                const hasData = total > 0;
                return (
                  <div className="flex flex-wrap items-center justify-center gap-8 mb-6 bg-white pt-4 border-t-2 border-gray-300">
                    {/* Horizontal bar chart - weekly cost per schedule */}
                    <div className="flex flex-col min-w-[360px]">
                      <div className="text-xs font-semibold text-gray-600 mb-2">Weekly cost by schedule</div>
                      <div className="flex gap-4 mb-1.5 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#2563eb]" /> Hourly</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#0d9488]" /> Salary</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        {scheduleCosts.length === 0 ? (
                          <div className="text-sm text-gray-400 py-2">No schedule costs this week</div>
                        ) : (
                          scheduleCosts.map(({ scheduleId, name, cost, hourly: schedHourly, salary: schedSalary }) => {
                            const totalBarW = (cost / maxScheduleCost) * barChartWidth;
                            const hourlyBarW = cost > 0 ? (schedHourly / cost) * totalBarW : 0;
                            const salaryBarW = cost > 0 ? (schedSalary / cost) * totalBarW : 0;
                            return (
                              <div key={scheduleId} className="flex items-center gap-2" style={{ height: barHeight + barGap }}>
                                <div className="text-xs text-gray-700 truncate font-medium" style={{ width: labelWidth }} title={name}>
                                  {name}
                                </div>
                                <div className="flex-1 flex items-center gap-2" style={{ maxWidth: barChartWidth + 60 }}>
                                  <div className="h-4 bg-gray-200 rounded overflow-hidden flex" style={{ width: barChartWidth }}>
                                    {schedHourly > 0 && (
                                      <div
                                        className="h-full bg-[#2563eb] transition-all"
                                        style={{ width: Math.max(hourlyBarW, 2) }}
                                        title={`Hourly: €${schedHourly.toFixed(2)}`}
                                      />
                                    )}
                                    {schedSalary > 0 && (
                                      <div
                                        className="h-full bg-[#0d9488] transition-all"
                                        style={{ width: Math.max(salaryBarW, 2) }}
                                        title={`Salary: €${schedSalary.toFixed(2)}`}
                                      />
                                    )}
                                  </div>
                                  <span className="text-xs font-semibold text-gray-700 tabular-nums whitespace-nowrap">
                                    €{cost.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <svg width={size} height={size} className="flex-shrink-0">
                        {hasData ? (
                          <>
                            {hourly > 0 && (
                              <path
                                d={segmentPath(0, deg(hourly))}
                                fill="#2563eb"
                                stroke="white"
                                strokeWidth={1.5}
                              />
                            )}
                            {salary > 0 && (
                              <path
                                d={segmentPath(deg(hourly), 360)}
                                fill="#0d9488"
                                stroke="white"
                                strokeWidth={1.5}
                              />
                            )}
                          </>
                        ) : (
                          <path
                            d={segmentPath(0, 360)}
                            fill="#e5e7eb"
                            stroke="white"
                            strokeWidth={1.5}
                          />
                        )}
                        <circle cx={cx} cy={cy} r={innerR - 2} fill="white" />
                        <text
                          x={cx}
                          y={cy - 6}
                          textAnchor="middle"
                          className="text-sm font-semibold fill-gray-700"
                        >
                          Total
                        </text>
                        <text
                          x={cx}
                          y={cy + 10}
                          textAnchor="middle"
                          className="text-lg font-bold fill-gray-900"
                        >
                          €{total.toFixed(2)}
                        </text>
                      </svg>
                      <span className="text-xs text-gray-500 mt-1">Weekly cost</span>
                    </div>
                    <div className="flex flex-col gap-3 min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-[#2563eb] flex-shrink-0" aria-hidden />
                        <div>
                          <div className="text-sm font-semibold text-gray-800">Hourly Employees</div>
                          <div className="text-sm text-gray-600">€{hourly.toFixed(2)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-[#0d9488] flex-shrink-0" aria-hidden />
                        <div>
                          <div className="text-sm font-semibold text-gray-800">Salary Employees</div>
                          <div className="text-sm text-gray-600">€{salary.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Close Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowScheduleBreakdownModal(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
              </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ScheduleCalendar;

